<script setup lang="ts">
/**
 * 產品（憑證設計＋套裝）管理頁——批93 §2.1 F。
 *
 * ## 這一頁在管什麼
 *
 * 「憑證設計」＝**這個產品有哪些模組鍵／配額鍵、各自什麼意思**；「套裝」＝那些鍵的
 * 一組預設組合（輕量級／標準級／…／試用）。批93 之前這兩件事寫死在後端
 * `services/issue.py` 的三個常數裡，只服務得了 iplusone-cs 一個產品。
 *
 * ## 權限
 *
 * 讀＝admin（`RequestsView`／`LicensesView` 要靠憑證設計把模組鍵翻成中文，admin 得看得到）；
 * **寫＝superadmin**（改一份憑證設計＝改之後每一張核發出去的證）。所有寫操作的入口一律
 * 掛 `v-if="auth.isManagerOrAbove"`——真正的防線在後端，這裡只是不給人白按。
 *
 * ## 版型（主人裁示）
 *
 * 左產品清單＋右三頁籤【基本資料】【憑證設計】【套裝】；憑證設計是**表格式＋列編輯彈窗**
 * （與後台既有的表格＋UiModal 慣例一致），整包 `PUT /schema` 一次存。
 */
import { computed, onMounted, ref, watch } from 'vue'

import HostingProfileCard from '@/components/HostingProfileCard.vue'
import ModulePicker from '@/components/ModulePicker.vue'
import PolicyEditor from '@/components/PolicyEditor.vue'
import ProductParamsPanel from '@/components/ProductParamsPanel.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { ModuleSpec, PlanSpec, PolicySpec, ProductSpec, QuotaSpec } from '@/api/ls'
import * as productIo from '@/api/productIo'
import type { ProductDiff, ProductDocument } from '@/api/productIo'
import { buildLimits, limitRowsOf, limitsSummary } from '@/lib/addonLimits'
import type { LimitRow } from '@/lib/addonLimits'
import { knownErrorText } from '@/lib/apiErrors'
import { vEnter } from '@/lib/enterKey'
import { PROFILE_KEY, profileState } from '@/lib/hostingProfile'
import { numOr } from '@/lib/numField'
import { PARAMS_OPTION_KEY, PARAMS_SEQ_OPTION_KEY } from '@/lib/productParams'
import {
  ADDON_MERGE_DEFAULT,
  ADDON_MERGE_LABELS,
  ADDON_MERGE_MODES,
  ADDON_STEP_MAX,
  USAGE_ALERT_MAX_PCTS,
  fmtAlertPcts,
  parseAddonStep,
  parseAlertPcts,
  quotaAddonMerge,
  quotaAddonStep,
  quotaAllowsUnlimited,
  quotaCustomerRequestable,
  quotaRequired,
  usageMetricValid,
  withAddonMerge,
  withAddonStep,
  withUsageAlert,
} from '@/lib/quotas'
import { addonStepError } from '@/lib/addonQuotaInput'
import type { AddonMergeMode } from '@/lib/quotas'
import { formatTerm, splitMinutes, totalMinutes } from '@/lib/terms'
import {
  ACTIVATION_POLICY_LABELS,
  OPTION_FALLBACKS,
  isInternalModule,
  optionOf,
  planKindLabel,
  productStatusLabel,
} from '@/lib/products'
import type { ProductOptions } from '@/lib/products'
import { ROLE_LABELS, roleLabel } from '@/lib/roles'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const auth = useAuthStore()
const dialogs = useDialogsStore()

const selectedKey = ref<string>('')
const selected = computed<ProductSpec | null>(
  () => store.products.find((p) => p.key === selectedKey.value) ?? null,
)
/**
 * 「預設核發政策」下拉的值域——**刻意只有兩個**（無 `reseller`）：這是「沒有套裝可比對」
 * 的客製內容 fallback，無模板可比的內容不該對外部角色放行（批93 §2.2）。
 * 中文名一律查 `ACTIVATION_POLICY_LABELS`（批97 Bug 4）。
 */
const FALLBACK_POLICIES = ['superadmin', 'admin'] as const

type Tab = 'basic' | 'schema' | 'plans' | 'params' | 'hosting'
const tab = ref<Tab>('basic')
const TABS: { key: Tab; label: string }[] = [
  { key: 'basic', label: '基本資料' },
  { key: 'schema', label: '憑證設計' },
  { key: 'plans', label: '套裝' },
  // 批151 C2：產品參數版本（宣告＋版本）。自己的端點、自己的權限分界（讀 admin、寫營運主管）。
  { key: 'params', label: '產品參數版本' },
  // 批141（裁示 14）：代管設定（`options.hosting_profile`）。自己一個頁籤、自己的存檔端點
  // ——它的驗證比憑證設計嚴格得多（錯一格＝開出去的站目錄錯、channel 錯）。
  { key: 'hosting', label: '代管設定' },
]

/**
 * 開放由我方代管、卻沒有有效的代管設定＝**其實開不了站**（後端開站一律 422
 * `hosting_profile_missing`）。清單、憑證設計的勾選框旁與代管設定頁籤三處都用紅字標出來。
 */
function hostingBroken(p: ProductSpec | null | undefined): boolean {
  return !!p && p.options?.supports_hosting === true && profileState(p.options) !== 'ok'
}

async function fail(e: unknown, fallback: string) {
  const detail = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  let msg = fallback
  // 批155：跨畫面共用的錯誤碼（自動核准、內部套裝、子證上限…）先查中文對照。
  const known = knownErrorText(detail)
  if (known) msg = known
  else if (typeof detail === 'string') msg = detail
  else if (detail && typeof detail === 'object') {
    const d = detail as { message?: unknown; code?: unknown }
    msg = typeof d.message === 'string' ? d.message : JSON.stringify(detail)
  }
  await dialogs.notify({ title: '操作失敗', message: msg })
}

async function reload(keepKey = true) {
  const prev = selectedKey.value
  await store.loadProducts()
  if (!keepKey || !store.products.some((p) => p.key === prev)) {
    selectedKey.value = store.products[0]?.key ?? ''
  }
}

onMounted(async () => {
  try {
    await store.ensureProducts()
  } catch (e) {
    await fail(e, '讀取產品清單失敗')
  }
  selectedKey.value = store.products[0]?.key ?? ''
})

// ---------------------------------------------------------------------------
// ① 基本資料
// ---------------------------------------------------------------------------
const basic = ref({ name: '', status: 'active', signing_kid: '', notes: '' })
function resetBasic() {
  const p = selected.value
  basic.value = {
    name: p?.name ?? '',
    status: p?.status ?? 'active',
    signing_kid: p?.signing_kid ?? '',
    notes: p?.notes ?? '',
  }
}
const basicSaving = ref(false)
async function saveBasic() {
  const p = selected.value
  if (!p || !basic.value.name.trim()) return
  basicSaving.value = true
  try {
    await ls.updateProduct(p.id, {
      name: basic.value.name.trim(),
      status: basic.value.status,
      signing_kid: basic.value.signing_kid.trim() || null,
      notes: basic.value.notes.trim() || null,
    })
    await reload()
    await dialogs.notify({ title: '已儲存', message: '產品基本資料已更新。' })
  } catch (e) {
    await fail(e, '儲存失敗')
  } finally {
    basicSaving.value = false
  }
}

// ---------------------------------------------------------------------------
// ② 憑證設計（草稿：整包編輯、一次 PUT /schema）
// ---------------------------------------------------------------------------
const draftModules = ref<ModuleSpec[]>([])
const draftQuotas = ref<QuotaSpec[]>([])
// 批109 立了這一欄但沒做編輯 UI；批110 補上——#5494「各套裝不同寬限期」的作法就是
// 「產品宣告一個 `grace_days` 行為參數鍵 ＋ 各套裝填自己的預設值」，沒有 UI 等於沒做。
const draftPolicies = ref<PolicySpec[]>([])
/**
 * 批114 裁示 ㉖：這三個產品選項**後台不再編輯**——值改由配額鍵的宣告推導
 * （後端 `services/quota_compat.derived_product_options`：有沒有宣告那個鍵、鍵的中文名）。
 *
 * ★ 兩份真相必然漂移：勾了「不按人頭賣」卻仍宣告那個配額鍵的產品，後台表單不畫那一欄、
 *   核發卻照樣要求它。所以草稿不收、存檔也不送這三個鍵（後端 `strip_derived_options`
 *   會忽略它們並保留 DB 既有值，送了也不會出事，但留著只是讓 JSON 多一份會過期的舊值）。
 */
const DERIVED_OPTION_KEYS = ['seat_label', 'supports_seats', 'supports_max_product_lines'] as const
type EditableOptions = Omit<ProductOptions, (typeof DERIVED_OPTION_KEYS)[number]>
function editableOptionDefaults(): EditableOptions {
  // 解構去鍵（有 rest 兄弟時 TS 不會把這幾個當成未使用變數）。
  const { seat_label, supports_seats, supports_max_product_lines, ...rest } = OPTION_FALLBACKS
  return rest
}
/**
 * 產品選項的表單狀態。**逐欄具名**而不是 `Record<string, unknown>`——模板裡的 v-model
 * 需要有型別的 lvalue（`v-model="opts.x as string"` 不是合法的 v-model 表達式）。
 * 憑證設計裡我們不認得的選項鍵放 `extraOptions`，存檔時原樣併回去（不靜默丟掉）。
 */
const draftOptions = ref<EditableOptions>(editableOptionDefaults())
const extraOptions = ref<Record<string, unknown>>({})
/** 有專屬頁籤與端點的選項鍵——〈憑證設計〉的整包存檔不帶它們（後端也會忽略並保留現值）。 */
const SCHEMA_SKIPPED_OPTION_KEYS = new Set<string>([PROFILE_KEY, PARAMS_OPTION_KEY, PARAMS_SEQ_OPTION_KEY])
/** 批153：市集型錄產品代號（與後端 `plugin_registry.CATALOG_KEY_RE`、發布主機型錄 key 同一條規則）。 */
const CATALOG_KEY_RE = /^[a-z][a-z0-9-]{0,31}$/
/** 期限選項在畫面上是逗號字串（`30, 365`），存檔時再切回數字陣列。 */
const termOptionsText = ref('')
/**
 * 批108 的**分鐘級**期限選項（`options.term_options`，單位總分鐘）。畫面上同樣是逗號
 * 字串，但每一項用 `TermField` 的語彙顯示不了 → 直接收數字，旁邊給一行說明。
 * 留空＝這個產品不用分鐘級選項，`termMinuteOptions()` 會退回上面那格 ×1440。
 */
const termMinuteOptionsText = ref('')

function resetSchema() {
  const p = selected.value
  draftModules.value = JSON.parse(JSON.stringify(p?.modules ?? []))
  draftQuotas.value = JSON.parse(JSON.stringify(p?.quotas ?? []))
  draftPolicies.value = JSON.parse(JSON.stringify(p?.policies ?? []))
  draftOptions.value = {
    grace_days: Number(optionOf(p, 'grace_days')),
    default_term_days: Number(optionOf(p, 'default_term_days')),
    default_max_deployments: Number(optionOf(p, 'default_max_deployments')),
    term_day_options: optionOf(p, 'term_day_options'),
    term_options: optionOf(p, 'term_options'),
    supports_deployment_mode: !!optionOf(p, 'supports_deployment_mode'),
    hosted_trial_term_days: Number(optionOf(p, 'hosted_trial_term_days')),
    default_activation_policy: String(optionOf(p, 'default_activation_policy')),
    // 批99-A：缺鍵＝false（fail-closed 破例，見 `lib/products.ts::OPTION_FALLBACKS`）。
    supports_hosting: !!optionOf(p, 'supports_hosting'),
    // 批153：plugin 市集（同樣 fail-closed）與市集網頁掛在型錄的哪個產品底下。
    supports_plugin_market: optionOf(p, 'supports_plugin_market') === true,
    market_catalog_key: (optionOf(p, 'market_catalog_key') as string | null) ?? null,
  }
  const extra: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(p?.options ?? {})) {
    // 推導出來的三個鍵也不進 `extraOptions`——它們在 `OPTION_FALLBACKS` 裡，本來就不會
    // 落到這裡；寫明是為了讓「這一頁不碰那三個鍵」在程式上是一句話。
    // ★ 批141：代管設定也不進來——它有自己的頁籤與端點；後端的 schema PUT 會忽略它，
    //   但原樣帶著送回去只會讓人以為「儲存憑證設計」也存了代管設定。
    // ★ 批151：參數宣告與它的版本號計數同理——各自有專屬端點，後端的 schema PUT 會忽略並
    //   保留現值；不帶回去才不會讓人以為「儲存憑證設計」也存了它們。
    if (!(k in OPTION_FALLBACKS) && !SCHEMA_SKIPPED_OPTION_KEYS.has(k)) extra[k] = v
  }
  extraOptions.value = extra
  termOptionsText.value = (optionOf(p, 'term_day_options') ?? []).join(', ')
  termMinuteOptionsText.value = (optionOf(p, 'term_options') ?? []).join(', ')
}

/** 換產品／換頁籤都把草稿重設回伺服器上的內容（避免把 A 的設計存到 B 身上）。 */
watch(selected, () => {
  resetBasic()
  resetSchema()
})
watch(tab, () => {
  if (tab.value === 'schema') resetSchema()
})

const schemaSaving = ref(false)
async function saveSchema() {
  const p = selected.value
  if (!p) return
  const terms = termOptionsText.value
    .split(/[,，\s]+/)
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
  const termMins = termMinuteOptionsText.value
    .split(/[,，\s]+/)
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
  const options: Record<string, unknown> = {
    ...extraOptions.value,
    ...draftOptions.value,
    term_day_options: terms.length ? terms : OPTION_FALLBACKS.term_day_options,
    // 批108：留空就存空陣列（＝沿用上面那格 ×1440），不要塞兜底值進去。
    term_options: termMins,
    grace_days: Number(draftOptions.value.grace_days) || OPTION_FALLBACKS.grace_days,
    default_term_days:
      Number(draftOptions.value.default_term_days) || OPTION_FALLBACKS.default_term_days,
    default_max_deployments:
      Number(draftOptions.value.default_max_deployments) ||
      OPTION_FALLBACKS.default_max_deployments,
    hosted_trial_term_days:
      Number(draftOptions.value.hosted_trial_term_days) ||
      OPTION_FALLBACKS.hosted_trial_term_days,
    // 批153：空字串＝null（不出市集網頁）。
    market_catalog_key: String(draftOptions.value.market_catalog_key ?? '').trim() || null,
  }
  if (options.market_catalog_key && !CATALOG_KEY_RE.test(String(options.market_catalog_key))) {
    await dialogs.notify({
      title: '型錄產品代號格式不對',
      message: '只能用小寫英數與連字號、英文字母開頭、最多 32 字（留空＝不出市集網頁）。',
    })
    return
  }
  schemaSaving.value = true
  try {
    const res = await ls.updateProductSchema(p.id, {
      modules: draftModules.value as unknown as Record<string, unknown>[],
      quotas: draftQuotas.value as unknown as Record<string, unknown>[],
      policies: draftPolicies.value as unknown as Record<string, unknown>[],
      options,
    })
    await reload()
    resetSchema()
    // 批141：存檔成功但帶提醒碼——開放代管卻沒有有效的代管設定（不擋存檔，但要講）。
    const missing = (res?.warnings ?? []).includes('hosting_profile_missing')
    await dialogs.notify({
      title: '已儲存憑證設計',
      message:
        '之後核發的授權會依這份設計帶入預設值與檢查模組鍵；既有授權不受影響。' +
        (missing
          ? '\n\n⚠ 注意：這個產品已開放由我方代管，但沒有有效的代管設定——目前開不了站。請到〈代管設定〉頁籤補齊。'
          : ''),
    })
  } catch (e) {
    await fail(e, '儲存憑證設計失敗')
  } finally {
    schemaSaving.value = false
  }
}

// --- 模組列編輯彈窗 ---
const modOpen = ref(false)
/** null＝新增；數字＝編輯 draftModules 的第幾列。 */
const modIndex = ref<number | null>(null)
const modDraft = ref<ModuleSpec>({
  key: '',
  label: '',
  kind: 'binary',
  items: [],
  default_scope: '*',
  deprecated: false,
})
function openModule(i: number | null) {
  modIndex.value = i
  modDraft.value =
    i == null
      ? { key: '', label: '', kind: 'binary', items: [], default_scope: '*', deprecated: false }
      : JSON.parse(JSON.stringify(draftModules.value[i]))
  modOpen.value = true
}
function addItem() {
  modDraft.value.items.push({ key: '', label: '', selectable: true })
}
function removeItem(i: number) {
  modDraft.value.items.splice(i, 1)
}
const modValid = computed(() => !!modDraft.value.key.trim() && !!modDraft.value.label.trim())
function applyModule() {
  if (!modValid.value) return
  // 批155：`internal` 只在 true 時寫（後端投影也是缺鍵＝false），免得每一列都多一個 false。
  const { internal: _internal, ...rest } = modDraft.value
  const row: ModuleSpec = {
    ...rest,
    ...(modDraft.value.internal === true ? { internal: true } : {}),
    key: modDraft.value.key.trim(),
    label: modDraft.value.label.trim(),
    default_scope: modDraft.value.default_scope.trim() || '*',
    items:
      modDraft.value.kind === 'scoped'
        ? modDraft.value.items
            .filter((it) => it.key.trim())
            .map((it) => ({
              key: it.key.trim(),
              label: it.label.trim() || it.key.trim(),
              selectable: it.selectable,
            }))
        : [],
  }
  if (modIndex.value == null) draftModules.value.push(row)
  else draftModules.value[modIndex.value] = row
  modOpen.value = false
}
async function removeModule(i: number) {
  const row = draftModules.value[i]
  if (
    !(await dialogs.confirm({
      title: '移除模組',
      message: `從憑證設計移除「${row.label}」（${row.key}）？\n既有授權上的這個鍵不會消失，但之後核發的表單不再有這一列，清單也會把它顯示成原始英文代號。要保留說明請改標「已停用」。`,
      danger: true,
      confirmText: '移除',
    }))
  )
    return
  draftModules.value.splice(i, 1)
}

// --- 配額列編輯彈窗 ---
//
// ★ 批114（裁示 ⑬㉓㉕）：一個配額鍵除了「叫什麼、缺席怎麼算」之外，還宣告**三個規則
//   旗標**——批114 之前這三件事是 LS 規則層寫死在某一個鍵上的內建特例（那個鍵必填、
//   不可無上限、客戶可以在自助申請單裡填），第二個產品每一條都會擋路。
//   新建的預設是「不必填、可無上限、客戶不可申請」（裁示 ㉕）：最寬鬆、最不會意外擋住
//   核發，要收緊是明確的動作。
const quotaOpen = ref(false)
const quotaIndex = ref<number | null>(null)
function blankQuota(): QuotaSpec {
  return {
    key: '',
    label: '',
    absent_default: null,
    usage_source: null,
    hint: null,
    deprecated: false,
    required: false,
    allow_unlimited: true,
    customer_requestable: false,
  }
}
const quotaDraft = ref<QuotaSpec>(blankQuota())
/**
 * 批151（裁示 98）：子證合併方式。另開一個有型別的 ref 綁下拉（`QuotaSpec.addon_merge` 是
 * 選填字串）；載入時缺鍵＝`max`，回存時 `max` 不送（`withAddonMerge`）。
 */
const quotaMerge = ref<AddonMergeMode>(ADDON_MERGE_DEFAULT)
/**
 * 批169（裁示 81）：用量告警門檻與精確用量指標。兩格都是文字，存檔時才解析
 * （`parseAlertPcts`／`withUsageAlert`）；空＝不告警／不用，回存時拿掉鍵。
 */
const quotaAlertText = ref('')
const quotaMetricText = ref('')
const quotaAlertPcts = computed(() => parseAlertPcts(quotaAlertText.value))
/**
 * 加購數量級距（11-01 裁示）：文字欄，存檔時才解析（`parseAddonStep`／`withAddonStep`）。
 * 空白＝不限制（回存時拿掉鍵）；`undefined`＝格式不合（擋住確定鈕）。
 */
// `<input type="number">` 的 v-model 會被 Vue 轉成 number（清空時又是 `''`），型別兩者都收。
const quotaStepText = ref<string | number>('')
const quotaStep = computed(() => parseAddonStep(quotaStepText.value))
function openQuota(i: number | null) {
  quotaIndex.value = i
  quotaDraft.value =
    i == null
      ? blankQuota()
      : // 舊憑證設計沒有這三個鍵 → 用與 `lib/quotas.ts` 三支 helper 相同的缺鍵預設補上，
        // 否則勾選框綁到 undefined，一開視窗就把「未宣告」顯示成「false」。
        // ★ 整份複製再補鍵：這一版前端不認得的鍵（日後新增的宣告欄位）也原樣帶回去。
        { ...blankQuota(), ...JSON.parse(JSON.stringify(draftQuotas.value[i])) }
  quotaDraft.value.required = quotaRequired(quotaDraft.value)
  quotaDraft.value.allow_unlimited = quotaAllowsUnlimited(quotaDraft.value)
  quotaDraft.value.customer_requestable = quotaCustomerRequestable(quotaDraft.value)
  quotaMerge.value = quotaAddonMerge(quotaDraft.value)
  quotaAlertText.value = fmtAlertPcts(quotaDraft.value.usage_alert_pcts)
  quotaMetricText.value = quotaDraft.value.usage_metric ?? ''
  quotaStepText.value = String(quotaAddonStep(quotaDraft.value) ?? '')
  quotaOpen.value = true
}
const quotaValid = computed(
  () =>
    !!quotaDraft.value.key.trim() &&
    !!quotaDraft.value.label.trim() &&
    quotaAlertPcts.value !== null &&
    usageMetricValid(quotaMetricText.value) &&
    quotaStep.value !== undefined,
)
function applyQuota() {
  if (!quotaValid.value) return
  const row: QuotaSpec = withAddonStep(
    withUsageAlert(
      withAddonMerge(
        {
          ...quotaDraft.value,
          key: quotaDraft.value.key.trim(),
          label: quotaDraft.value.label.trim(),
          hint: (quotaDraft.value.hint ?? '').toString().trim() || null,
          usage_source: (quotaDraft.value.usage_source ?? '').toString().trim() || null,
          required: !!quotaDraft.value.required,
          allow_unlimited: quotaDraft.value.allow_unlimited !== false,
          customer_requestable: !!quotaDraft.value.customer_requestable,
        },
        quotaMerge.value,
      ),
      quotaAlertPcts.value ?? [],
      quotaMetricText.value,
    ),
    quotaStep.value ?? null,
  )
  if (quotaIndex.value == null) draftQuotas.value.push(row)
  else draftQuotas.value[quotaIndex.value] = row
  quotaOpen.value = false
}
async function removeQuota(i: number) {
  const row = draftQuotas.value[i]
  if (
    !(await dialogs.confirm({
      title: '移除配額',
      message: `從憑證設計移除「${row.label}」（${row.key}）？既有授權上的這個鍵不會消失，只是之後的表單與清單不再認得它。`,
      danger: true,
      confirmText: '移除',
    }))
  )
    return
  draftQuotas.value.splice(i, 1)
}

// --- 行為參數的鍵定義（批110）---
//
// ★★ **值域是必填的**（`number` 要 min/max、`string` 要 options）：行為參數刻意不進政策閘的
//    偏離判準（每改一個數字就升一級權限等於沒人用得動），所以它的濫用防線就是「宣告時把
//    值域鎖死、簽發時照著校驗」。後端 `_assert_policy_declarations` 會再擋一次。
const policyOpen = ref(false)
const policyIndex = ref<number | null>(null)
const policyDraft = ref<PolicySpec>({
  key: '',
  label: '',
  type: 'number',
  absent_default: null,
  options: [],
  min: null,
  max: null,
  hint: null,
  deprecated: false,
})
/** `string` 型的可選值在畫面上是逗號字串。 */
const policyOptionsText = ref('')

function openPolicy(i: number | null) {
  policyIndex.value = i
  policyDraft.value =
    i == null
      ? {
          key: '',
          label: '',
          type: 'number',
          absent_default: null,
          options: [],
          min: 0,
          max: 100,
          hint: null,
          deprecated: false,
        }
      : JSON.parse(JSON.stringify(draftPolicies.value[i]))
  // 可產生唯一值與可選值不可並存（舊資料若兩者都有，存回時可選值會被清掉——後端本來就收不下）。
  policyOptionsText.value = policyDraft.value.generatable ? '' : (policyDraft.value.options ?? []).join(', ')
  policyOpen.value = true
}

const policyValid = computed(() => {
  const d = policyDraft.value
  if (!d.key.trim() || !d.label.trim()) return false
  if (d.type === 'number') {
    return typeof d.min === 'number' && typeof d.max === 'number' && d.min <= d.max
  }
  // 批151（裁示 112＋實作期裁示）：勾了「可產生唯一值」的字串參數**不列可選值**（自由字串）；
  // 兩者不可並存（後端 422 `generatable_with_options`）——勾選時就清空並停用可選值欄。
  if (d.type === 'string') return d.generatable === true || policyOptionsText.value.split(',').some((x) => x.trim())
  return true
})

/** 勾「可產生唯一值」＝清空可選值（產生的編號不會在清單裡，列了簽發一定被退回）。 */
function onGeneratableToggle() {
  if (policyDraft.value.generatable) policyOptionsText.value = ''
}

function applyPolicy() {
  if (!policyValid.value) return
  const d = policyDraft.value
  // `generatable` 只對字串有效（後端對其他型別帶 true 會 422）；false 一律不送（投影也只在 true 時出現）。
  const { generatable: _g, ...rest } = d
  const row: PolicySpec = {
    ...rest,
    key: d.key.trim(),
    label: d.label.trim(),
    hint: (d.hint ?? '').toString().trim() || null,
    // 只有對應型別的那組值域會被送出去（後端的宣告校驗也是按 type 判）；可產生唯一值的一律不列。
    options:
      d.type === 'string' && d.generatable !== true
        ? policyOptionsText.value.split(',').map((x) => x.trim()).filter(Boolean)
        : [],
    min: d.type === 'number' ? Number(d.min) : null,
    max: d.type === 'number' ? Number(d.max) : null,
    ...(d.type === 'string' && d.generatable === true ? { generatable: true } : {}),
  }
  if (policyIndex.value == null) draftPolicies.value.push(row)
  else draftPolicies.value[policyIndex.value] = row
  policyOpen.value = false
}

async function removePolicy(i: number) {
  const row = draftPolicies.value[i]
  if (
    !(await dialogs.confirm({
      title: '移除行為參數',
      message: `從憑證設計移除「${row.label}」（${row.key}）？既有授權上的這個鍵不會消失，只是之後的表單不再認得它（產品端會退回自己的預設）。`,
      danger: true,
      confirmText: '移除',
    }))
  )
    return
  draftPolicies.value.splice(i, 1)
}

/** 值域的一句話摘要（清單那一欄）。 */
function policyRangeText(p: PolicySpec): string {
  if (p.type === 'number') return `${p.min ?? '—'} ～ ${p.max ?? '—'}`
  if (p.type === 'string') return (p.options ?? []).join('、') || (p.generatable ? '不限（自由字串）' : '—')
  return '開／關'
}

// --- 拖曳排序（模組／配額共用；順序＝核發表單的欄序）---
const dragKind = ref<'module' | 'quota' | null>(null)
const dragFrom = ref<number | null>(null)
function onDragStart(kind: 'module' | 'quota', i: number) {
  dragKind.value = kind
  dragFrom.value = i
}
function move<T>(list: T[], from: number, to: number) {
  const [row] = list.splice(from, 1)
  list.splice(to, 0, row)
}
function onDrop(kind: 'module' | 'quota', to: number) {
  const from = dragFrom.value
  dragFrom.value = null
  if (dragKind.value !== kind || from == null || from === to) return
  if (kind === 'module') move(draftModules.value, from, to)
  else move(draftQuotas.value, from, to)
}

function itemsSummary(m: ModuleSpec): string {
  if (m.kind !== 'scoped') return `預設範圍 ${m.default_scope}`
  if (!m.items.length) return '（未列子項）'
  return m.items.map((it) => (it.selectable ? it.label : `${it.label}（不可勾）`)).join('、')
}

// ---------------------------------------------------------------------------
// ③ 套裝
// ---------------------------------------------------------------------------
const planOpen = ref(false)
const planEditing = ref<PlanSpec | null>(null)
const planDraft = ref<ls.PlanInput>({
  key: '',
  label: '',
  kind: 'plan',
  sort_order: 0,
  is_active: true,
  term_days: null,
  term_minutes: 0,
  modules: {},
  quotas: null,
  policies: {},
  activation_policy: 'superadmin',
  trial: false,
  auto_approve: false,
  internal: false,
  addon_limits_json: null,
  follow_main_expiry: false,
})
/**
 * 批155：子證上限的編輯列（每個配額鍵一列、max／max_delta 兩欄；兩欄都空＝不開放平台填）。
 * 只在種類＝加購時畫、只在種類＝加購時送（其他種類一律送 null）。
 */
const limitRows = ref<LimitRow[]>([])
const limitsBuilt = computed(() => buildLimits(limitRows.value))
/** ModulePicker／QuotaEditor 的重掛計數（`initFrom` 只在掛載時跑）。 */
const planFormKey = ref(0)
/**
 * 套裝期限的**總分鐘**（批108）；`null`＝留空＝「依核發時選擇」。
 * 送出時用 `splitMinutes` 拆回 `term_days`／`term_minutes` 兩欄。
 */
const planTermTotal = ref<number | null>(null)

function openPlan(p: PlanSpec | null) {
  const spec = selected.value
  planEditing.value = p
  // ★ 批114：那兩個數字**不再有獨立欄位**，一律走下面的 `QuotaEditor`。套裝的 `quotas`
  //   原樣帶入（`null`＝這個套裝一個鍵都不預設＝全客製），「某個鍵不放」的語意也靠它
  //   ——編輯器把必填鍵留空時就不輸出那個鍵，企業級套裝存回去仍然是「由操作者自己填」。
  planDraft.value = p
    ? {
        key: p.key,
        label: p.label,
        kind: p.kind,
        sort_order: p.sort_order,
        is_active: p.is_active,
        term_days: p.term_days,
        term_minutes: p.term_minutes,
        modules: { ...p.modules },
        quotas: p.quotas ? { ...p.quotas } : null,
        policies: { ...(p.policies ?? {}) },
        activation_policy: p.activation_policy,
        trial: p.trial,
        auto_approve: p.auto_approve === true,
        internal: p.internal === true,
        addon_limits_json: p.addon_limits_json ? JSON.parse(JSON.stringify(p.addon_limits_json)) : null,
        follow_main_expiry: p.follow_main_expiry === true,
      }
    : {
        key: '',
        label: '',
        kind: 'plan',
        sort_order: (spec?.plans.length ?? 0) + 1,
        is_active: true,
        term_days: null,
        term_minutes: 0,
        modules: {},
        quotas: null,
        policies: {},
        activation_policy: 'superadmin',
        trial: false,
        auto_approve: false,
        internal: false,
        addon_limits_json: null,
        follow_main_expiry: false,
      }
  limitRows.value = limitRowsOf(spec, planDraft.value.addon_limits_json)
  // ★ `term_days === null` 才是「沒設期限」；`0` 是合法值（分鐘級套裝）——用 truthy
  //   判斷會讓「2 小時體驗」一開編輯視窗就變成空白。
  planTermTotal.value =
    planDraft.value.term_days == null
      ? null
      : totalMinutes(planDraft.value.term_days, planDraft.value.term_minutes)
  planFormKey.value++
  planOpen.value = true
}

const planIsAddon = computed(() => planDraft.value.kind === 'addon')
const planIsPlan = computed(() => planDraft.value.kind === 'plan')
/** 批155：這個套裝（不是內部套裝）勾了內部模組——外部路徑會擋、平台送件不會自動核准。 */
const planHasInternalModule = computed(
  () =>
    !planDraft.value.internal &&
    (selected.value?.modules ?? []).some((m) => isInternalModule(m) && planDraft.value.modules[m.key] != null),
)
/**
 * 加購數量級距（11-01 裁示）：加購模板的配額值（正數）必須是各鍵宣告級距的倍數；null／0 是「不設定」與
 * 「留給送件填」，不擋（同後端 `assert_addon_template_steps`）。回第一個不合的那一項（畫面紅字），沒有＝`null`。
 */
const planStepIssue = computed<string | null>(() => {
  if (!planIsAddon.value) return null
  const fields = selected.value?.quotas ?? []
  for (const [k, v] of Object.entries(planDraft.value.quotas ?? {})) {
    const f = fields.find((x) => x.key === k)
    const msg = addonStepError(f, v, { allowUnset: true })
    if (msg) return `「${f?.label ?? k}」${msg}`
  }
  return null
})
const planValid = computed(
  () =>
    !!planDraft.value.key.trim() &&
    !!planDraft.value.label.trim() &&
    !(planIsAddon.value && limitsBuilt.value.error) &&
    !planStepIssue.value,
)
const planSaving = ref(false)
async function savePlan() {
  const spec = selected.value
  if (!spec || !planValid.value) return
  const d = planDraft.value
  const body: ls.PlanInput = {
    ...d,
    key: d.key.trim(),
    label: d.label.trim(),
    sort_order: numOr(d.sort_order, 0),
    // 批108：期限是兩欄——UI 收的是總分鐘，這裡拆回去。留空＝不設期限（兩欄都是空／0）。
    term_days: planTermTotal.value == null ? null : splitMinutes(planTermTotal.value)[0],
    term_minutes: planTermTotal.value == null ? 0 : splitMinutes(planTermTotal.value)[1],
    // 批114：相容一級欄位一律不送（後端 `_plan_quotas` 只會在有值時折進 quotas，
    // 送 null 等於什麼都沒說，但留著會讓下一個人以為表單還有那兩欄）。
    // 批155：自動核准只對種類「套裝」有意義（其他種類送 true 後端 422）；子證上限與跟隨主證
    // 只對種類「加購」有意義——換了種類之後，畫面上看不到的值不該被偷偷存回去。
    auto_approve: planIsPlan.value && d.auto_approve === true,
    internal: d.internal === true,
    addon_limits_json: planIsAddon.value ? limitsBuilt.value.value : null,
    follow_main_expiry: planIsAddon.value && d.follow_main_expiry === true,
  }
  // 兩個開關只有營運主管以上能改（後端也擋）。不是的話乾脆不送，後端保留現值。
  if (!auth.isManagerOrAbove) {
    delete body.auto_approve
    delete body.internal
  }
  planSaving.value = true
  try {
    if (planEditing.value) await ls.updatePlan(spec.id, planEditing.value.id, body)
    else await ls.createPlan(spec.id, body)
    planOpen.value = false
    await reload()
  } catch (e) {
    await fail(e, '儲存套裝失敗')
  } finally {
    planSaving.value = false
  }
}
async function removePlan(p: PlanSpec) {
  const spec = selected.value
  if (!spec) return
  if (
    !(await dialogs.confirm({
      title: '刪除套裝',
      message: `刪除套裝「${p.label}」（${p.key}）？零引用會直接刪掉；還有授權或申請掛這個套裝名時會先擋下來，再問你要不要強制刪。要停售請改成「停用」。`,
      danger: true,
      confirmText: '刪除',
    }))
  )
    return
  try {
    await ls.deletePlan(spec.id, p.id)
    await reload()
  } catch (e) {
    // 409 `plan_in_use`＝有授權／申請引用 → 問要不要強制刪（代價寫在訊息裡）。
    const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: never }).detail : null
    const obj = (d ?? {}) as { code?: string; licenses?: number; license_requests?: number }
    if (obj.code !== 'plan_in_use') {
      await fail(e, '刪除套裝失敗')
      return
    }
    const ok = await dialogs.confirm({
      title: '這個套裝還有人在用',
      message:
        `「${p.label}」已被 ${obj.licenses ?? 0} 張授權、${obj.license_requests ?? 0} 筆申請引用。\n\n` +
        '強制刪除之後，那些證的「套裝」欄仍會顯示這個名字，但**再也查不到它當初代表什麼內容**' +
        '（續約時的政策解析會退到最嚴）。套裝內容會寫進稽核紀錄備查。\n\n確定要強制刪除嗎？',
      danger: true,
      confirmText: '強制刪除',
    })
    if (!ok) return
    try {
      await ls.deletePlan(spec.id, p.id, true)
      await reload()
    } catch (e2) {
      await fail(e2, '強制刪除套裝失敗')
    }
  }
}

// ---------------------------------------------------------------------------
// 刪除產品（批110）
// ---------------------------------------------------------------------------
//
// ★★ 產品 key **逐字寫進每一張已簽發憑證的簽章明文**。刪掉之後那些證的模組／配額鍵就再也
//    翻不回中文、續約時的政策解析也找不到套裝列。所以這裡有三道：預覽（列出六張表的引用
//    筆數）、打字輸入完整 key、以及後端把整份憑證設計寫進稽核（`product.delete`）。
const delOpen = ref(false)
const delPreview = ref<ls.ProductDeletionPreview | null>(null)
const delTyped = ref('')
const delBusy = ref(false)

/** 六張弱關聯表的中文名（刪除預覽用）。 */
const REF_LABELS: Record<string, string> = {
  licenses: '授權',
  license_requests: '授權申請',
  license_addons: '加購子憑證',
  host_sites: '代管站台',
  redeem_campaigns: '兌換活動',
  redemptions: '兌換紀錄',
  // 批153：產品底下登記的市集 plugin（R1-7）。
  plugins: '市集擴充',
}
function refLabel(key: string): string {
  return REF_LABELS[key] ?? key
}
/**
 * 會跟著產品一起刪掉的「產品自己的資料」（不是引用、不擋刪除，所以不在 `references` 裡）：
 * 套裝＋產品參數版本（批151 R1-7：參數版本原本沒列出來，按下去才發現一起不見了）。
 * 都是 0 回空字串＝整句不顯示。
 */
function cascadeText(pv: ls.ProductDeletionPreview): string {
  const bits: string[] = []
  if (pv.plans) bits.push(`${pv.plans} 個套裝`)
  if (pv.param_versions) bits.push(`${pv.param_versions} 個產品參數版本`)
  return bits.join('、')
}

async function openDelete() {
  const spec = selected.value
  if (!spec) return
  delTyped.value = ''
  delPreview.value = null
  delOpen.value = true
  try {
    delPreview.value = await ls.productDeletionPreview(spec.id)
  } catch (e) {
    delOpen.value = false
    await fail(e, '讀取刪除預覽失敗')
  }
}

async function doDelete() {
  const spec = selected.value
  const pv = delPreview.value
  if (!spec || !pv || delTyped.value.trim() !== spec.key) return
  delBusy.value = true
  try {
    await ls.deleteProduct(spec.id, !pv.deletable)
    delOpen.value = false
    selectedKey.value = ''
    await reload()
  } catch (e) {
    await fail(e, '刪除產品失敗')
  } finally {
    delBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 新增產品／以此為樣板
// ---------------------------------------------------------------------------
const createOpen = ref(false)
const createForm = ref({ key: '', name: '', notes: '' })
const createBusy = ref(false)
function openCreate() {
  createForm.value = { key: '', name: '', notes: '' }
  createOpen.value = true
}
async function doCreate() {
  if (!createForm.value.key.trim() || !createForm.value.name.trim()) return
  createBusy.value = true
  try {
    const p = await ls.createProduct({
      key: createForm.value.key.trim(),
      name: createForm.value.name.trim(),
      notes: createForm.value.notes.trim() || null,
    })
    createOpen.value = false
    await reload(false)
    selectedKey.value = p.key
    tab.value = 'schema'
  } catch (e) {
    await fail(e, '建立產品失敗')
  } finally {
    createBusy.value = false
  }
}

const cloneOpen = ref(false)
const cloneForm = ref({ key: '', name: '', include_plans: true })
const cloneBusy = ref(false)
function openClone() {
  cloneForm.value = { key: '', name: '', include_plans: true }
  cloneOpen.value = true
}
async function doClone() {
  const src = selected.value
  if (!src || !cloneForm.value.key.trim() || !cloneForm.value.name.trim()) return
  cloneBusy.value = true
  try {
    const p = await ls.cloneProduct(src.id, {
      key: cloneForm.value.key.trim(),
      name: cloneForm.value.name.trim(),
      include_plans: cloneForm.value.include_plans,
    })
    cloneOpen.value = false
    await reload(false)
    selectedKey.value = p.key
    tab.value = 'schema'
    await dialogs.notify({
      title: '已建立',
      message: `已以「${src.name}」為樣板建立「${p.name}」。簽章金鑰（kid）與代管設定刻意不複製——新產品預設沿用系統金鑰，代管設定請到〈代管設定〉頁籤另行建立。`,
    })
  } catch (e) {
    await fail(e, '複製產品失敗')
  } finally {
    cloneBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 匯出／匯入（批107 項 7）
// ---------------------------------------------------------------------------
//
// 搬家流程：在 A 套 LS 按【匯出】拿到一份 JSON → 到 B 套 LS 按【匯入】選檔 →
// **先看差異**（每個產品一列，紅字是會讓人後悔的那幾種變更）→ 勾選要套用的 → 確認。
//
// 三件在 UI 上要講清楚的事（後端也各自有保護，這裡只是不讓人意外）：
// ① 簽章金鑰 kid、「開放由我方代管」、產品狀態（上架／停售）**不會**被匯入。
// ② 套裝只會新增與更新，**不刪除**。
// ③ 按下確認時後端會**重新算一次差異**——預覽到確認之間別人可能動過產品頁。

const exportBusy = ref(false)
async function doExport(key?: string) {
  exportBusy.value = true
  try {
    const doc = await productIo.exportProducts(key)
    productIo.downloadJson(productIo.exportFilename(key), doc)
  } catch (e) {
    await fail(e, '匯出失敗')
  } finally {
    exportBusy.value = false
  }
}

const importInput = ref<HTMLInputElement | null>(null)
const importOpen = ref(false)
const importBusy = ref(false)
const importFileName = ref('')
/** 選到的那份檔案（原樣保留——送回後端套用時要送同一份）。 */
const importDoc = ref<ProductDocument | null>(null)
const importDiffs = ref<ProductDiff[]>([])
/** 勾選要套用的產品 key。 */
const importPicked = ref<Record<string, boolean>>({})

/** 可勾選＝真的會寫入的那些（`unchanged` 沒東西可寫、`forbidden` 不在你的範圍內）。 */
function importSelectable(d: ProductDiff): boolean {
  return d.action === 'create' || d.action === 'update'
}
const importPickedKeys = computed(() =>
  importDiffs.value.filter((d) => importPicked.value[d.key]).map((d) => d.key),
)
const IMPORT_ACTION_LABELS: Record<string, string> = {
  create: '新建',
  update: '更新',
  unchanged: '無變更',
  forbidden: '無權限',
}

function summarizeDiff(d: ProductDiff): string {
  if (d.action === 'unchanged') return '與這裡的設計完全相同。'
  if (d.action === 'forbidden') return '不在你的產品範圍內。'
  const bits: string[] = []
  const list = (label: string, x: ProductDiff['modules']) => {
    const parts: string[] = []
    if (x.added.length) parts.push(`新增 ${x.added.length}`)
    if (x.removed.length) parts.push(`移除 ${x.removed.length}`)
    if (x.changed.length) parts.push(`修改 ${x.changed.length}`)
    if (x.reordered) parts.push('順序調整')
    if (parts.length) bits.push(`${label} ${parts.join('／')}`)
  }
  list('模組', d.modules)
  list('配額', d.quotas)
  const opts =
    Object.keys(d.options.added).length +
    Object.keys(d.options.removed).length +
    Object.keys(d.options.changed).length
  if (opts) bits.push(`選項 ${opts} 項`)
  if (d.plans.created.length) bits.push(`套裝新增 ${d.plans.created.length}`)
  if (d.plans.updated.length) bits.push(`套裝更新 ${d.plans.updated.length}`)
  if (d.plans.extra_in_target.length) bits.push(`保留 ${d.plans.extra_in_target.length} 個既有套裝`)
  // 批141：代管設定另立一欄（不混進一般選項的差異）。
  const hp = d.hosting_profile
  if (hp?.change === 'added') bits.push('代管設定新增')
  else if (hp?.change === 'removed') bits.push('代管設定移除')
  else if (hp?.change === 'changed') bits.push(`代管設定修改（${hp.fields.join('、')}）`)
  // 裁示 48：channel 是本台專用的值，變化直接寫出來（提醒文字在 warnings）。
  if (hp?.channel) bits.push(`出貨 channel ${hp.channel.from ?? '（無）'} → ${hp.channel.to}`)
  // 批151：參數宣告同樣另立一欄（參數版本本身不隨檔案搬家）。
  const vp = d.versioned_params
  if (vp?.change === 'added') bits.push(`參數宣告新增（${vp.added.length} 個參數鍵）`)
  else if (vp?.change === 'removed') bits.push('參數宣告移除')
  else if (vp?.change === 'changed') {
    const parts: string[] = []
    if (vp.added.length) parts.push(`新增 ${vp.added.join('、')}`)
    if (vp.removed.length) parts.push(`移除 ${vp.removed.join('、')}`)
    if (vp.changed.length) parts.push(`修改 ${vp.changed.join('、')}`)
    bits.push(`參數宣告修改${parts.length ? `（${parts.join('；')}）` : '（順序調整）'}`)
  }
  if (Object.keys(d.basic).length) bits.push('基本資料')
  return bits.length ? bits.join('、') : '僅基本資料異動。'
}

function resetImport() {
  importDoc.value = null
  importDiffs.value = []
  importPicked.value = {}
  importFileName.value = ''
  if (importInput.value) importInput.value.value = ''
}

async function onImportFile(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return
  importFileName.value = file.name
  importBusy.value = true
  try {
    const text = await file.text()
    const parsed: unknown = JSON.parse(text)
    if (!productIo.looksLikeProductDocument(parsed)) {
      resetImport()
      await dialogs.notify({
        title: '這不是產品設計匯出檔',
        message: `檔案裡找不到 format="${productIo.PRODUCT_DOC_FORMAT}"。請用另一套授權伺服器的〈匯出〉按鈕產生的 JSON。`,
      })
      return
    }
    const res = await productIo.previewProductImport(parsed)
    importDoc.value = parsed
    importDiffs.value = res.products
    // 預設勾選所有「真的會改到東西」的產品；紅字的那些也勾著，但下方會標紅要人看過。
    importPicked.value = Object.fromEntries(
      res.products.filter(importSelectable).map((d) => [d.key, true]),
    )
    importOpen.value = true
  } catch (e) {
    resetImport()
    if (e instanceof SyntaxError) {
      await dialogs.notify({ title: '檔案不是合法的 JSON', message: '請確認整個檔案都完整無缺。' })
    } else if (!(await failProfileImport(e))) {
      await fail(e, '讀取匯入檔失敗')
    }
  } finally {
    importBusy.value = false
    // 同一個檔案再選一次也要觸發 change（不清空的話瀏覽器不會發事件）。
    if (importInput.value) importInput.value.value = ''
  }
}

/**
 * 匯入檔裡某個產品的代管設定驗不過（批141：後端 422 `hosting_profile_invalid`，帶
 * `product`／`field`）。整份檔案都不會被套用——講清楚是哪個產品的哪一格。
 * 回傳 true＝已處理。
 */
async function failProfileImport(e: unknown): Promise<boolean> {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  const obj = (d ?? {}) as { code?: string; message?: string; field?: string }
  if (obj.code !== 'hosting_profile_invalid') return false
  await dialogs.notify({
    title: '匯入檔的代管設定不合格式',
    message:
      `${obj.message ?? '代管設定不合格式'}${obj.field ? `（欄位 ${obj.field}）` : ''}` +
      '\n\n整份檔案都沒有被套用。請修正匯入檔後再試一次。',
  })
  return true
}

async function doImport() {
  const doc = importDoc.value
  const keys = importPickedKeys.value
  if (!doc || !keys.length) return
  const dangerous = importDiffs.value.filter((d) => keys.includes(d.key) && d.dangers.length)
  if (dangerous.length) {
    const ok = await dialogs.confirm({
      title: '確認套用這些變更',
      // 對話框是純文字渲染（不過 markdown），別在字串裡夾 `**`。
      message:
        `有 ${dangerous.length} 個產品帶著需要注意的變更（刪除模組／配額鍵、核發政策放寬、` +
        '套裝種類或試用旗標改變）。這些只影響「之後核發」的證，既有授權不受影響。要繼續嗎？',
      danger: true,
      confirmText: '套用',
    })
    if (!ok) return
  }
  importBusy.value = true
  try {
    const res = await productIo.applyProductImport(doc, keys)
    const created = (res.applied ?? []).filter((a) => a.action === 'create').length
    const updated = (res.applied ?? []).filter((a) => a.action === 'update').length
    importOpen.value = false
    resetImport()
    await reload()
    await dialogs.notify({
      title: '匯入完成',
      message: `新建 ${created} 個、更新 ${updated} 個產品。簽章金鑰、「開放由我方代管」與產品狀態維持這裡的設定；套裝只新增與更新，不會被刪除。`,
    })
  } catch (e) {
    if (!(await failProfileImport(e))) await fail(e, '匯入失敗')
  } finally {
    importBusy.value = false
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>產品</h1>
      <span class="count">{{ store.products.length }}</span>
      <span class="spacer" />
      <!-- 匯出是讀（admin 以上）、匯入是寫（superadmin）——與本頁其餘操作同一條分界。 -->
      <UiButton v-if="auth.isAdminOrAbove" :loading="exportBusy" @click="doExport()">
        匯出全部
      </UiButton>
      <UiButton v-if="auth.isManagerOrAbove" :loading="importBusy" @click="importInput?.click()">
        匯入
      </UiButton>
      <input
        ref="importInput"
        type="file"
        accept=".json,application/json"
        hidden
        @change="onImportFile"
      />
      <UiButton v-if="auth.isManagerOrAbove" variant="primary" @click="openCreate">＋ 新增產品</UiButton>
    </div>

    <p v-if="!auth.isManagerOrAbove" class="ro-banner">
      你的角色是{{ roleLabel(auth.role) }}——本頁<strong>唯讀</strong>。憑證設計與套裝的異動會影響之後每一張核發出去的證，
      只有{{ ROLE_LABELS.manager }}以上能改。
    </p>

    <div class="grid">
      <!-- 左：產品清單 -->
      <div class="card">
        <div class="card-head"><h3 class="section-title">產品</h3></div>
        <ul class="list">
          <li
            v-for="p in store.products"
            :key="p.key"
            :class="{ sel: selectedKey === p.key }"
            @click="selectedKey = p.key"
          >
            <span class="pname">{{ p.name }}</span>
            <code class="mono">{{ p.key }}</code>
            <small v-if="p.status !== 'active'" class="dep-tag">{{ productStatusLabel(p.status) }}</small>
            <small v-if="hostingBroken(p)" class="hp-broken" title="開放由我方代管，但沒有有效的代管設定">缺代管設定</small>
            <small class="cnt">{{ p.modules.length }} 模組・{{ p.quotas.length }} 配額・{{ p.plans.length }} 套裝</small>
          </li>
        </ul>
        <div v-if="!store.products.length" class="empty">
          <div class="empty-sub">還沒有任何產品。</div>
        </div>
      </div>

      <!-- 右：三頁籤 -->
      <div v-if="selected" class="card">
        <div class="tabs" data-hscroll>
          <button
            v-for="t in TABS"
            :key="t.key"
            type="button"
            class="tab"
            :class="{ active: tab === t.key }"
            @click="tab = t.key"
          >
            {{ t.label }}
          </button>
        </div>

        <!-- ① 基本資料 -->
        <template v-if="tab === 'basic'">
          <div class="form-grid two">
            <label class="field">
              <span class="field-label">產品代號（key）</span>
              <input :value="selected.key" disabled />
              <small class="subtle">建立後不可更改——它被逐字寫進每一張已簽發授權的明文。</small>
            </label>
            <label class="field">
              <span class="field-label">名稱</span>
              <input v-model="basic.name" :disabled="!auth.isManagerOrAbove" maxlength="120" />
            </label>
            <label class="field">
              <span class="field-label">狀態</span>
              <select v-model="basic.status" :disabled="!auth.isManagerOrAbove">
                <option value="active">販售中</option>
                <option value="deprecated">已停售</option>
              </select>
              <small class="subtle">停售＝核發下拉不再出現；歷史授權照樣查得到。要真的清掉請用下面的「刪除產品」。</small>
            </label>
            <label class="field">
              <span class="field-label">簽章金鑰 kid（選填）</span>
              <input
                v-model="basic.signing_kid"
                :disabled="!auth.isManagerOrAbove"
                placeholder="留空＝沿用系統金鑰"
                maxlength="32"
              />
            </label>
          </div>
          <label class="field notes">
            <span class="field-label">備註</span>
            <textarea v-model="basic.notes" :disabled="!auth.isManagerOrAbove" rows="3" />
          </label>
          <div class="acts-row">
            <!--
              批110：刪除產品。**產品 key 逐字寫進每一張已簽發憑證的簽章明文**——先看引用筆數、
              再打字輸入完整 key。零引用（誤建的測試產品）直接刪得掉；有引用時後端會擋，要顯式強制。
              ★ 批112（主人 09-17）：頁面上**只放一顆一般按鈕**（不紅、不藏、不配警語）——紅框＋整段
                警語擺在儲存列下面太礙眼；「按了才跳出來警告」，警語全部在確認視窗裡。
                位置＝這一列的**最左邊**（主人：「移到左下」）：與右邊的儲存／還原隔開一整列的寬度，
                不會跟儲存按錯，也不另佔一列。
            -->
            <UiButton v-if="auth.isManagerOrAbove" class="del-btn" @click="openDelete">刪除產品…</UiButton>
            <!-- 匯出是讀：admin 也能把設計帶去另一套 LS（寫入那一端才要 superadmin）。 -->
            <UiButton
              v-if="auth.isAdminOrAbove"
              :loading="exportBusy"
              @click="doExport(selected.key)"
            >
              匯出此產品
            </UiButton>
            <template v-if="auth.isManagerOrAbove">
              <UiButton @click="resetBasic">還原</UiButton>
              <UiButton variant="secondary" @click="openClone">以此為樣板建立新產品</UiButton>
              <UiButton variant="primary" :loading="basicSaving" @click="saveBasic">儲存</UiButton>
            </template>
          </div>
        </template>

        <!-- ② 憑證設計 -->
        <template v-else-if="tab === 'schema'">
          <div class="card-head sub">
            <h3 class="section-title">模組</h3>
            <span class="spacer" />
            <UiButton v-if="auth.isManagerOrAbove" size="sm" variant="secondary" @click="openModule(null)">
              ＋ 新增模組
            </UiButton>
          </div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th class="w-drag"></th><th>代號</th><th>顯示名稱</th><th>類型</th>
                  <th>子項／預設範圍</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(m, i) in draftModules"
                  :key="m.key + i"
                  :draggable="auth.isManagerOrAbove"
                  @dragstart="onDragStart('module', i)"
                  @dragover.prevent
                  @drop.prevent="onDrop('module', i)"
                >
                  <td class="w-drag">
                    <span v-if="auth.isManagerOrAbove" class="drag" title="拖曳調整順序（＝核發表單的欄序）">⋮⋮</span>
                  </td>
                  <td><code class="mono">{{ m.key }}</code></td>
                  <td>
                    {{ m.label }}
                    <span v-if="m.deprecated" class="chip dep">已停用</span>
                    <span v-if="isInternalModule(m)" class="chip int">內部</span>
                  </td>
                  <td class="nowrap">{{ m.kind === 'scoped' ? '白名單' : '開關' }}</td>
                  <td class="subtle">{{ itemsSummary(m) }}</td>
                  <td class="acts-cell">
                    <div v-if="auth.isManagerOrAbove" class="acts">
                      <button class="x" title="編輯" @click="openModule(i)">✎</button>
                      <button class="x danger" title="移除" @click="removeModule(i)">✕</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!draftModules.length">
                  <td colspan="6" class="subtle">尚未定義任何模組。</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-head sub">
            <h3 class="section-title">行為參數</h3>
            <span class="spacer" />
            <UiButton v-if="auth.isManagerOrAbove" size="sm" variant="secondary" @click="openPolicy(null)">
              ＋ 新增行為參數
            </UiButton>
          </div>
          <p class="subtle">
            第三種 entitlement：不是能力開關（模組）也不是數量上限（配額），而是「這套站台的
            行為參數」——例如寬限天數。<strong>值域在這裡就要鎖死</strong>（數值填最小／最大、
            字串列出可選值），簽發時會照著校驗。各套裝可以在〈套裝〉頁填自己的預設值。
          </p>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr><th>代號</th><th>顯示名稱</th><th>型別</th><th>值域</th><th>缺席預設</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in draftPolicies" :key="p.key + i">
                  <td><code class="mono">{{ p.key }}</code></td>
                  <td>
                    {{ p.label }}
                    <span v-if="p.deprecated" class="chip dep">已停用</span>
                    <span v-if="p.type === 'string' && p.generatable" class="chip gen" title="核發表單在這一格旁邊有「產生」鈕">可產生唯一值</span>
                  </td>
                  <td class="nowrap">{{ p.type }}</td>
                  <td class="subtle">{{ policyRangeText(p) }}</td>
                  <td class="subtle">{{ p.absent_default ?? '—' }}</td>
                  <td class="acts-cell">
                    <div v-if="auth.isManagerOrAbove" class="acts">
                      <button class="x" title="編輯" @click="openPolicy(i)">✎</button>
                      <button class="x danger" title="移除" @click="removePolicy(i)">✕</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!draftPolicies.length">
                  <td colspan="6" class="subtle">尚未定義任何行為參數。</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-head sub">
            <h3 class="section-title">配額</h3>
            <span class="spacer" />
            <UiButton v-if="auth.isManagerOrAbove" size="sm" variant="secondary" @click="openQuota(null)">
              ＋ 新增配額
            </UiButton>
          </div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th class="w-drag"></th><th>代號</th><th>顯示名稱</th>
                  <th>缺席預設</th><th>規則</th><th>用量來源</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(q, i) in draftQuotas"
                  :key="q.key + i"
                  :draggable="auth.isManagerOrAbove"
                  @dragstart="onDragStart('quota', i)"
                  @dragover.prevent
                  @drop.prevent="onDrop('quota', i)"
                >
                  <td class="w-drag">
                    <span v-if="auth.isManagerOrAbove" class="drag" title="拖曳調整順序">⋮⋮</span>
                  </td>
                  <td><code class="mono">{{ q.key }}</code></td>
                  <td>
                    {{ q.label }}
                    <span v-if="q.deprecated" class="chip dep">已停用</span>
                    <div v-if="q.hint" class="subtle hint">{{ q.hint }}</div>
                  </td>
                  <td>{{ q.absent_default == null ? '不限' : q.absent_default }}</td>
                  <!-- 批114：三個規則旗標。預設值（不必填／可無上限／客戶不可申請）不印，
                       只標出被收緊或放寬的那些——十一個鍵全印滿反而看不出哪個特別。 -->
                  <td>
                    <span v-if="quotaRequired(q)" class="chip req">必填</span>
                    <span v-if="!quotaAllowsUnlimited(q)" class="chip">不可無上限</span>
                    <span v-if="quotaCustomerRequestable(q)" class="chip">客戶可申請</span>
                    <!-- 批151：合併方式只標非預設的（預設＝取最大值，十一個鍵全印反而看不出哪個特別）。 -->
                    <span
                      v-if="quotaAddonMerge(q) !== ADDON_MERGE_DEFAULT"
                      class="chip"
                      title="主證與生效子證的同名配額怎麼合併"
                    >子證{{ ADDON_MERGE_LABELS[quotaAddonMerge(q)] }}</span>
                    <!-- 批169：用量告警門檻（只在有宣告時標）。 -->
                    <span
                      v-if="q.usage_alert_pcts?.length"
                      class="chip"
                      title="站台用量越過門檻時在〈告警〉產生一則，降回門檻以下自動解除"
                    >告警 {{ q.usage_alert_pcts.join('／') }}%</span>
                    <!-- 加購數量級距（只在有宣告時標）。 -->
                    <span
                      v-if="quotaAddonStep(q) != null"
                      class="chip"
                      title="加購子證上這一項只能填級距的倍數"
                    >加購每 {{ quotaAddonStep(q) }}</span>
                    <span
                      v-if="
                        !quotaRequired(q) &&
                        quotaAllowsUnlimited(q) &&
                        !quotaCustomerRequestable(q) &&
                        quotaAddonMerge(q) === ADDON_MERGE_DEFAULT &&
                        !q.usage_alert_pcts?.length &&
                        quotaAddonStep(q) == null
                      "
                      class="subtle"
                    >—</span>
                  </td>
                  <td class="subtle">
                    {{ q.usage_source ? '專屬心跳欄（' + q.usage_source + '）' : '用量快照' }}
                    <!-- 批169：精確用量指標（有宣告才印）。 -->
                    <div v-if="q.usage_metric" class="hint">精確值：指標 <code class="mono">{{ q.usage_metric }}</code></div>
                  </td>
                  <td class="acts-cell">
                    <div v-if="auth.isManagerOrAbove" class="acts">
                      <button class="x" title="編輯" @click="openQuota(i)">✎</button>
                      <button class="x danger" title="移除" @click="removeQuota(i)">✕</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!draftQuotas.length">
                  <td colspan="7" class="subtle">尚未定義任何配額。</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-head sub"><h3 class="section-title">產品選項</h3></div>
          <div class="form-grid three">
            <label class="field">
              <span class="field-label">寬限天數</span>
              <input v-model.number="draftOptions.grace_days" type="number" min="0" :disabled="!auth.isManagerOrAbove" />
            </label>
            <label class="field">
              <span class="field-label">預設期限（天）</span>
              <input v-model.number="draftOptions.default_term_days" type="number" min="1" :disabled="!auth.isManagerOrAbove" />
            </label>
            <label class="field">
              <span class="field-label">期限選項（天，逗號分隔）</span>
              <input v-model="termOptionsText" :disabled="!auth.isManagerOrAbove" placeholder="30, 365" />
            </label>
            <!-- 批108：分鐘級期限（技術支援救援、教育訓練的臨時憑證）。留空＝不用，
                 核發下拉就沿用上面那格換算。 -->
            <label class="field">
              <span class="field-label">期限選項（分鐘，逗號分隔）</span>
              <input
                v-model="termMinuteOptionsText"
                :disabled="!auth.isManagerOrAbove"
                placeholder="留空＝沿用上面的天數；例：30, 120, 43200"
              />
              <small class="subtle">
                總分鐘（1 天＝1440）。填了這格就以它為準，核發／申請的期限下拉直接讀它。
              </small>
            </label>
            <label class="field">
              <span class="field-label">預設部署數上限</span>
              <input v-model.number="draftOptions.default_max_deployments" type="number" min="1" :disabled="!auth.isManagerOrAbove" />
            </label>
            <!-- 代管試用天數在「開放代管」關掉時只是**弱化**（灰字＋註記），刻意不隱藏：
                 值還在 JSON 裡，藏起來會讓人以為被清掉了，日後開放代管又得重填。 -->
            <label class="field" :class="{ subtle: !draftOptions.supports_hosting }">
              <span class="field-label">代管試用天數</span>
              <input v-model.number="draftOptions.hosted_trial_term_days" type="number" min="1" :disabled="!auth.isManagerOrAbove" />
              <small v-if="!draftOptions.supports_hosting" class="subtle">僅在開放代管時生效。</small>
            </label>
            <label class="field">
              <span class="field-label">預設核發政策</span>
              <select v-model="draftOptions.default_activation_policy" :disabled="!auth.isManagerOrAbove">
                <!-- ★ 刻意**不提供 reseller**：這是「沒有套裝可比對」的客製內容用的 fallback，
                     無模板可比的內容不該對外部角色放行（批93 §2.2）。
                     文案走 `ACTIVATION_POLICY_LABELS`（批97 Bug 4：全中文的唯一真相）。 -->
                <option v-for="p in FALLBACK_POLICIES" :key="p" :value="p">
                  {{ ACTIVATION_POLICY_LABELS[p] }}{{ p === 'superadmin' ? '（預設）' : '' }}
                </option>
              </select>
              <small class="subtle">套裝各自有自己的核發政策；這裡是「沒有套裝」（客製內容）時的 fallback。</small>
            </label>
          </div>
          <div class="checks">
            <!-- 批114 裁示 ㉖：原本這裡還有兩格「支援某某上限／按人頭計價」與一格標籤，
                 值改由配額鍵的宣告推導（宣告了那個鍵＝支援，鍵的中文名＝標籤）。 -->
            <label class="chk">
              <input v-model="draftOptions.supports_deployment_mode" type="checkbox" :disabled="!auth.isManagerOrAbove" />
              支援部署模式（線上／離線氣隙）
            </label>
            <!-- 批99-A：整個代管中台的可見性與開站資格都以這一顆為準（員工側欄、portal
                 的「代管開站」卡、開站三重閘）。**缺鍵＝未開放**，不是「預設像 CS」。 -->
            <label class="chk">
              <input v-model="draftOptions.supports_hosting" type="checkbox" :disabled="!auth.isManagerOrAbove" />
              開放由我方代管
              <small class="subtle">還需主機上的 agent 支援此產品才開得成站。</small>
            </label>
            <!-- 批153：plugin 市集的產品能力旗標（缺鍵＝未開放，同代管那一顆）。沒開的產品不能登記
                 plugin、站台取不到私人清單與下載票、也不進市集。 -->
            <label class="chk">
              <input v-model="draftOptions.supports_plugin_market" type="checkbox" :disabled="!auth.isManagerOrAbove" />
              開放擴充市集
              <small class="subtle">站台可從市集安裝公開擴充，並取得指派給它的私人擴充。</small>
            </label>
          </div>
          <div class="form-grid two market-catalog">
            <label class="field">
              <span class="field-label">市集網頁掛在型錄的哪個產品</span>
              <input
                v-model="draftOptions.market_catalog_key"
                class="mono"
                maxlength="32"
                placeholder="型錄的產品代號；留空＝不出市集網頁"
                :disabled="!auth.isManagerOrAbove"
              />
              <small class="subtle">
                發布主機型錄（catalog.json）裡的產品代號，市集網頁會在「/代號/plugins/」。留空時清單 JSON 照常提供。
                <template v-if="!draftOptions.supports_plugin_market">僅在開放擴充市集時生效。</template>
              </small>
            </label>
          </div>
          <p v-if="draftOptions.supports_hosting && profileState(selected.options) !== 'ok'" class="hp-broken-text">
            ⚠ 這個產品還沒有有效的代管設定——開放代管之後仍然開不了站。請到〈代管設定〉頁籤補齊。
          </p>

          <div v-if="auth.isManagerOrAbove" class="save-bar">
            <p class="warn-text">
              ⚠ 儲存只影響<strong>之後核發</strong>的證；既有授權是簽好的快照，內容不會改變。
            </p>
            <div class="acts-row">
              <UiButton @click="resetSchema">取消</UiButton>
              <UiButton variant="primary" :loading="schemaSaving" @click="saveSchema">
                儲存憑證設計
              </UiButton>
            </div>
          </div>
        </template>

        <!-- ⑤ 產品參數版本（批151 C2） -->
        <template v-else-if="tab === 'params'">
          <ProductParamsPanel :product="selected" :can-edit="auth.isManagerOrAbove" @saved="reload()" />
        </template>

        <!-- ④ 代管設定（批141） -->
        <template v-else-if="tab === 'hosting'">
          <HostingProfileCard
            :product="selected"
            :products="store.products"
            :can-edit="auth.isManagerOrAbove"
            @saved="reload()"
          />
        </template>

        <!-- ③ 套裝 -->
        <template v-else>
          <div class="card-head sub">
            <h3 class="section-title">套裝</h3>
            <span class="spacer" />
            <UiButton v-if="auth.isManagerOrAbove" size="sm" variant="secondary" @click="openPlan(null)">
              ＋ 新增套裝
            </UiButton>
          </div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th>代號</th><th>名稱</th><th>種類</th><th class="w-quota">配額</th><th>期限</th>
                  <th>核發政策</th><th>狀態</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in selected.plans" :key="p.id">
                  <td><code class="mono">{{ p.key }}</code></td>
                  <td>
                    {{ p.label }}
                    <!-- 批155：內部／自動核准／子證上限／跟隨主證到期 -->
                    <div v-if="p.internal || p.auto_approve || p.follow_main_expiry || limitsSummary(p.addon_limits_json)" class="plan-flags">
                      <span v-if="p.internal" class="chip int">內部</span>
                      <span v-if="p.auto_approve" class="chip aa">自動核准</span>
                      <span v-if="limitsSummary(p.addon_limits_json)" class="chip">{{ limitsSummary(p.addon_limits_json) }}</span>
                      <span v-if="p.follow_main_expiry" class="chip">跟隨主證到期</span>
                    </div>
                  </td>
                  <td>{{ planKindLabel(p.kind) }}</td>
                  <!-- 批114：原本是硬編的席位欄。`quotas` 整包 null＝這個套裝一個鍵都不
                       預設（＝全客製，由操作者核發時自己填）；只缺某幾個鍵＝那幾項客製
                       （`template` 模式，不套 `absent_default`）。 -->
                  <td class="w-quota">
                    <span v-if="!p.quotas" class="subtle">客製</span>
                    <QuotaSummary v-else :spec="selected" :quotas="p.quotas" template />
                  </td>
                  <!-- 批108：`p.term_days` 可能是 0（分鐘級套裝）→ 不可用 truthy 判斷。 -->
                  <td>{{ p.term_days == null ? '依核發' : formatTerm(p.term_days, p.term_minutes) }}</td>
                  <td>
                    {{ ACTIVATION_POLICY_LABELS[p.activation_policy] ?? p.activation_policy }}
                    <div class="subtle policy-hint">
                      {{
                        p.activation_policy === 'superadmin'
                          ? `這個套裝的申請一律要${ROLE_LABELS.superadmin}審核核發`
                          : '選擇可免審核發＝該套裝的申請在確認送出時即簽發，不必再經審核'
                      }}
                    </div>
                  </td>
                  <td>
                    <span class="status-pill" :class="p.is_active ? 'active' : 'superseded'">
                      {{ p.is_active ? '啟用' : '停用' }}
                    </span>
                  </td>
                  <td class="acts-cell">
                    <div v-if="auth.isManagerOrAbove" class="acts">
                      <button class="x" title="編輯" @click="openPlan(p)">✎</button>
                      <button class="x danger" title="刪除" @click="removePlan(p)">✕</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!selected.plans.length">
                  <td colspan="8" class="subtle">這個產品還沒有任何套裝。</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="subtle">
            只有種類「套裝」且啟用的列會出現在核發表單的 chip 列；「試用」是代管開站用的模板、
            「加購」保留給子憑證（批93-B）。
          </p>
        </template>
      </div>
      <div v-else class="card">
        <div class="empty"><div class="empty-sub">請先在左側選一個產品。</div></div>
      </div>
    </div>

    <!-- 模組列編輯彈窗 -->
    <UiModal
      :open="modOpen"
      :title="modIndex == null ? '新增模組' : `編輯模組 — ${modDraft.label || modDraft.key}`"
      width="620px"
      @close="modOpen = false"
    >
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">代號（key）</span>
          <input v-model="modDraft.key" :disabled="modIndex != null" placeholder="例：calendar" />
          <small v-if="modIndex != null" class="subtle">建立後不可更改（既有授權帶著這個鍵）。</small>
        </label>
        <label class="field">
          <span class="field-label">顯示名稱</span>
          <input v-model="modDraft.label" placeholder="例：行事曆" />
        </label>
        <label class="field">
          <span class="field-label">類型</span>
          <select v-model="modDraft.kind">
            <option value="binary">開關（有鍵就是全開）</option>
            <option value="scoped">白名單（可只給部分子項）</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">預設範圍</span>
          <input v-model="modDraft.default_scope" placeholder="*" />
          <small class="subtle">勾「全部」時寫進授權的值，現行一律 <code class="mono">*</code>。</small>
        </label>
      </div>

      <template v-if="modDraft.kind === 'scoped'">
        <div class="card-head sub">
          <h4 class="section-title">子項</h4>
          <span class="spacer" />
          <UiButton size="sm" variant="secondary" @click="addItem">＋ 新增子項</UiButton>
        </div>
        <div v-for="(it, i) in modDraft.items" :key="i" class="item-row">
          <input v-model="it.key" class="it-key" placeholder="代號（如 line）" />
          <input v-model="it.label" class="it-label" placeholder="顯示名稱（如 LINE 官方帳號）" />
          <label class="chk">
            <input v-model="it.selectable" type="checkbox" />
            可勾選
          </label>
          <button class="x danger" title="移除子項" @click="removeItem(i)">✕</button>
        </div>
        <p v-if="!modDraft.items.length" class="subtle">還沒有子項——白名單模組至少要一個才選得動。</p>
        <p class="subtle">
          取消「可勾選」＝清單上看得到中文名、但新證不給勾（CS 的「人工進線」就是這種）。
        </p>
      </template>

      <label class="chk standalone">
        <input v-model="modDraft.deprecated" type="checkbox" />
        已停用（不再販售；既有授權仍顯示中文名，核發表單標記出來）
      </label>
      <!-- 批155：內部模組。存在憑證設計 JSONB 的 `internal` 鍵（不需要 migration）。 -->
      <label class="chk standalone">
        <input v-model="modDraft.internal" type="checkbox" />
        內部模組（任何對外清單都不列、外部申請帶到會被擋；後台只有營運主管以上能放進核發內容）
      </label>

      <template #foot>
        <UiButton @click="modOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!modValid" @click="applyModule">確定</UiButton>
      </template>
    </UiModal>

    <!-- 配額列編輯彈窗 -->
    <UiModal
      :open="quotaOpen"
      :title="quotaIndex == null ? '新增配額' : `編輯配額 — ${quotaDraft.label || quotaDraft.key}`"
      width="620px"
      @close="quotaOpen = false"
    >
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">代號（key）</span>
          <input v-model="quotaDraft.key" :disabled="quotaIndex != null" placeholder="例：max_devices" />
        </label>
        <label class="field">
          <span class="field-label">顯示名稱</span>
          <input v-model="quotaDraft.label" placeholder="例：裝置數" />
        </label>
        <label class="field">
          <span class="field-label">缺席預設</span>
          <select v-model="quotaDraft.absent_default">
            <option :value="null">不限（鍵缺席＝無上限）</option>
            <option :value="0">0（鍵缺席＝此授權不含這一項）</option>
          </select>
          <small class="subtle">
            產品端讀不到這個鍵時怎麼解讀。標成「必填」的鍵不會缺席，這一格對它沒有作用。
          </small>
        </label>
        <label class="field">
          <!-- ★ 批114：原本是寫死某個產品心跳欄名的下拉（LS 不為任何產品設計）。
               心跳欄名是那個產品自己的契約，只能由建產品的人填。 -->
          <span class="field-label">用量來源（心跳欄名）</span>
          <input v-model="quotaDraft.usage_source" placeholder="留空＝用量快照" />
          <small class="subtle">
            留空＝讀用量快照 <code class="mono">quotas_in_use[{{ quotaDraft.key || '鍵名' }}]</code>；
            填了就改讀心跳回報上的這個欄位（產品端要回報同名欄）。
          </small>
        </label>
        <!-- 批151（裁示 98）：主證與生效子證的同名配額怎麼合併。缺鍵＝取最大值（批93-B 起的規則）。 -->
        <label class="field">
          <span class="field-label">子證合併方式</span>
          <select v-model="quotaMerge">
            <option v-for="m in ADDON_MERGE_MODES" :key="m" :value="m">
              {{ ADDON_MERGE_LABELS[m] }}{{ m === ADDON_MERGE_DEFAULT ? '（預設）' : '' }}
            </option>
          </select>
          <small class="subtle">
            站台同時有主證與加購子證時，這一項的有效值怎麼算。「平均」四捨五入成整數；「最新一張」取簽發時間最晚的那張。
          </small>
        </label>
        <!-- 加購數量級距（11-01 裁示）：加購子證上這一項只能填級距的倍數。**不進授權**（簽發側的規則）。 -->
        <label class="field">
          <span class="field-label">加購級距（選填）</span>
          <input
            v-model="quotaStepText"
            type="number"
            min="1"
            :max="ADDON_STEP_MAX"
            step="1"
            placeholder="留空＝不限制"
          />
          <small v-if="quotaStep === undefined" class="err-text">
            請填 1～{{ ADDON_STEP_MAX }} 的整數；留空＝不限制。
          </small>
          <small v-else class="subtle">
            加購這一項時只能買級距的倍數（填 100＝只能 100、200、300…）；後台、代理商、平台與兌換試用碼都照它擋。
            既有的加購模板要先改成倍數才能設定；已簽出的子證續期、換機不受影響。
          </small>
        </label>
        <!-- 批169（裁示 81）：用量告警。門檻是我方的告警設定，**不進授權**（產品端看不到）。 -->
        <label class="field">
          <span class="field-label">用量告警門檻（%）</span>
          <input v-model="quotaAlertText" placeholder="例：90, 100（留空＝不告警）" />
          <small v-if="quotaAlertPcts === null" class="err-text">
            請填 1～100 的整數、以逗號分隔、不重複，最多 {{ USAGE_ALERT_MAX_PCTS }} 個。
          </small>
          <small v-else class="subtle">
            站台用量越過門檻時在〈告警〉產生一則（只留越過的最高一級：100% 以上為嚴重、其餘為警告），
            用量降回門檻以下自動解除。代理商看得到自家客戶的。
          </small>
        </label>
        <label class="field">
          <span class="field-label">精確用量指標（選填）</span>
          <input v-model="quotaMetricText" placeholder="留空＝用心跳的整數用量" />
          <small v-if="!usageMetricValid(quotaMetricText)" class="err-text">
            指標鍵名只能是小寫英文開頭、小寫英數與底線，最長 40 字。
          </small>
          <small v-else class="subtle">
            站台指標裡與這一項<strong>同單位</strong>、可帶小數的鍵。填了，告警與部署頁的使用率改用它
            （上限小的配額用整數算百分比會失真）；站台沒回報時退回整數用量。
          </small>
        </label>
      </div>
      <label class="field notes">
        <span class="field-label">說明（滑鼠停留時顯示）</span>
        <input v-model="quotaDraft.hint" placeholder="選填" />
      </label>
      <!-- 批114（裁示 ⑬㉓㉕）：規則旗標。新建的預設＝不必填、可無上限、客戶不可申請。
           三條都是**簽發側**的規則，產品端讀不到它們（產品端只看得到值）。 -->
      <div class="checks stack">
        <label class="chk">
          <input v-model="quotaDraft.required" type="checkbox" />
          必填（簽發時一定要有值，缺鍵會被伺服器退回）
        </label>
        <label class="chk">
          <input v-model="quotaDraft.allow_unlimited" type="checkbox" />
          可無上限（不勾＝核發表單不給「無上限」，一定要填數字）
        </label>
        <label class="chk">
          <input v-model="quotaDraft.customer_requestable" type="checkbox" />
          客戶可自行申請（出現在客戶自助 portal 的申請單上）
        </label>
      </div>
      <label class="chk standalone">
        <input v-model="quotaDraft.deprecated" type="checkbox" />
        已停用
      </label>
      <template #foot>
        <UiButton @click="quotaOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!quotaValid" @click="applyQuota">確定</UiButton>
      </template>
    </UiModal>

    <!-- 行為參數編輯彈窗（批110）-->
    <UiModal
      :open="policyOpen"
      :title="policyIndex == null ? '新增行為參數' : `編輯行為參數 — ${policyDraft.label || policyDraft.key}`"
      width="620px"
      @close="policyOpen = false"
    >
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">代號（key）</span>
          <input v-model="policyDraft.key" :disabled="policyIndex != null" placeholder="例：grace_days" />
        </label>
        <label class="field">
          <span class="field-label">顯示名稱</span>
          <input v-model="policyDraft.label" placeholder="例：寬限天數" />
        </label>
        <label class="field">
          <span class="field-label">型別</span>
          <select v-model="policyDraft.type">
            <option value="number">數值</option>
            <option value="bool">開關</option>
            <option value="string">字串（列舉）</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">缺席預設</span>
          <input v-model="policyDraft.absent_default" placeholder="產品端讀不到這個鍵時用的值" />
        </label>
        <template v-if="policyDraft.type === 'number'">
          <label class="field">
            <span class="field-label">最小值</span>
            <input v-model.number="policyDraft.min" type="number" />
          </label>
          <label class="field">
            <span class="field-label">最大值</span>
            <input v-model.number="policyDraft.max" type="number" />
          </label>
        </template>
        <label v-if="policyDraft.type === 'string'" class="field notes">
          <span class="field-label">可選值（逗號分隔）</span>
          <input
            v-model="policyOptionsText"
            :disabled="policyDraft.generatable === true"
            :placeholder="policyDraft.generatable ? '不列可選值（自由字串）' : '例：strict, lenient'"
          />
        </label>
      </div>
      <!-- 批151（裁示 112＋實作期裁示）：只對字串型顯示。勾了之後核發表單在這一格旁邊有「產生」鈕
           （填入 YYYYMMDD-xxxx）；與可選值不可並存——勾選即清空並停用上面那格。 -->
      <label v-if="policyDraft.type === 'string'" class="chk standalone">
        <input v-model="policyDraft.generatable" type="checkbox" @change="onGeneratableToggle" />
        可產生唯一值（核發表單在這一格旁邊給「產生」鈕，填入「日期－4 碼亂數」；勾了就不列可選值）
      </label>
      <p class="subtle">
        ★ <strong>值域是必填的</strong>：行為參數刻意不進核發政策的「偏離模板」判準
        （每改一個數字就升一級權限等於沒人用得動），所以它的護欄就是這裡宣告的範圍——
        簽發與換發時會照著校驗，超出一律 422。
      </p>
      <label class="field notes">
        <span class="field-label">說明（滑鼠停留時顯示）</span>
        <input v-model="policyDraft.hint" placeholder="選填" />
      </label>
      <label class="chk standalone">
        <input v-model="policyDraft.deprecated" type="checkbox" />
        已停用
      </label>
      <template #foot>
        <UiButton @click="policyOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!policyValid" @click="applyPolicy">確定</UiButton>
      </template>
    </UiModal>

    <!-- 套裝編輯彈窗 -->
    <UiModal
      :open="planOpen"
      :title="planEditing ? `編輯套裝 — ${planDraft.label}` : '新增套裝'"
      width="820px"
      @close="planOpen = false"
    >
      <div class="form-grid three">
        <label class="field">
          <span class="field-label">代號（key）</span>
          <input v-model="planDraft.key" placeholder="例：standard" />
        </label>
        <label class="field">
          <span class="field-label">名稱</span>
          <input v-model="planDraft.label" placeholder="例：標準級" />
        </label>
        <label class="field">
          <span class="field-label">種類</span>
          <select v-model="planDraft.kind">
            <option value="plan">套裝（可販售）</option>
            <option value="trial">試用（代管開站模板）</option>
            <option value="addon">加購（子憑證模板）</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">期限</span>
          <!-- 批108：數值＋單位下拉（天／小時／分鐘）。留空＝依核發時選擇。 -->
          <TermField v-model="planTermTotal" nullable />
        </label>
        <label class="field">
          <span class="field-label">排序</span>
          <input v-model="planDraft.sort_order" type="number" min="0" />
        </label>
        <label class="field">
          <span class="field-label">核發政策</span>
          <select v-model="planDraft.activation_policy">
            <option v-for="(label, key) in ACTIVATION_POLICY_LABELS" :key="key" :value="key">
              {{ label }}
            </option>
          </select>
        </label>
        <div class="field">
          <span class="field-label">狀態</span>
          <div class="field-row">
            <label class="chk"><input v-model="planDraft.is_active" type="checkbox" /> 啟用</label>
            <label class="chk"><input v-model="planDraft.trial" type="checkbox" /> 試用旗標</label>
          </div>
        </div>
      </div>
      <p class="subtle policy-note">
        選擇「可免審核發」＝該套裝的申請在確認送出時即簽發，不必再經審核；
        內容偏離套裝模板時仍會自動升為「{{ ACTIVATION_POLICY_LABELS.superadmin }}」（批93-C）。
      </p>

      <!-- 批155：兩個開關都只有營運主管以上能改。說明固定寫在各自的勾選框底下（不隨狀態出現／消失），
           切換種類或勾選時版面不動；不適用的種類直接停用勾選框。 -->
      <h4 class="sub-title">平台送件與可見範圍</h4>
      <div class="checks stack">
        <div class="chk-block">
          <label class="chk" :class="{ off: !auth.isManagerOrAbove || !planIsPlan }">
            <!-- 種類不是「套裝」時畫成未勾（存檔也一律送 false），切回「套裝」時原本的勾選還在。 -->
            <input
              type="checkbox"
              :checked="planIsPlan && !!planDraft.auto_approve"
              :disabled="!auth.isManagerOrAbove || !planIsPlan"
              @change="planDraft.auto_approve = ($event.target as HTMLInputElement).checked"
            />
            自動核准（只對平台送件）
          </label>
          <small class="chk-note">
            只適用於種類「套裝」。要與〈合作平台〉的平台層開關同時打開，而且送件內容逐字等於這個套裝的模板、
            期限在允許的清單內，平台送的申請才會當場核准；任一不符照常轉人工審核。
          </small>
        </div>
        <div class="chk-block">
          <label class="chk" :class="{ off: !auth.isManagerOrAbove }">
            <input v-model="planDraft.internal" type="checkbox" :disabled="!auth.isManagerOrAbove" />
            內部套裝
          </label>
          <small class="chk-note">
            任何對外清單都不列、外部申請（平台、客戶自助、代理商）送不進來；後台只有營運主管以上能拿它核發或換證。
          </small>
        </div>
      </div>
      <p v-if="!auth.isManagerOrAbove" class="subtle policy-note">
        這兩個開關只有「{{ ROLE_LABELS.manager }}」以上能改。
      </p>

      <h4 class="sub-title">模組</h4>
      <ModulePicker
        :key="`m${planFormKey}`"
        v-model="planDraft.modules"
        :spec="selected"
        :allow-internal="auth.isManagerOrAbove"
      />
      <p v-if="planHasInternalModule" class="warn-text policy-note">
        ⚠ 這個套裝勾了內部模組：外部申請選到它會被擋下，平台送件也不會自動核准。
        只給內部使用的話，請把套裝本身也標成「內部套裝」。
      </p>
      <h4 class="sub-title">數量配額</h4>
      <!-- ★ 批114：套裝**刻意不接 `update:valid`**——「某個必填鍵留空」在套裝上是合法的，
           語意是「由操作者核發時自己填」（企業級套裝就是這樣）。接了就存不起來。 -->
      <!-- ★ 刻意**不傳** `addon-context`：加購模板的 null（不設定）與 0（留給送件填，平台契約）都是
           有意義的值，加購語境「只送正數」會把它們洗掉。級距檢查改由下方的 `planStepIssue` 提示並擋存檔。 -->
      <QuotaEditor :key="`q${planFormKey}`" v-model="planDraft.quotas" :spec="selected" />
      <p v-if="planStepIssue" class="err-text policy-note">{{ planStepIssue }}</p>
      <p class="subtle policy-note">
        留空的必填鍵不會寫進套裝＝由操作者核發時自己填；其餘欄位一律帶入這裡填的值。
      </p>

      <!-- 批155：子證模板（種類＝加購）才有的兩件事：平台可以填哪些配額、上限多少；跟隨主證到期。 -->
      <template v-if="planIsAddon">
        <h4 class="sub-title">子證上限（平台送件）</h4>
        <p class="subtle">
          有填上限的項目＝平台可以替組織填這一項的數值；一列兩欄都空＝不開放，平台只能照模板。
          超過上限時平台那邊會轉人工處理，後台同時收到告警。
        </p>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table lim">
            <thead>
              <tr><th>配額</th><th class="lim-col">單張上限</th><th class="lim-col">增量上限</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in limitRows" :key="r.key" :class="{ bad: r.invalid }">
                <td>
                  {{ r.label }} <code class="mono">{{ r.key }}</code>
                  <span v-if="r.invalid" class="chip dep">已停用或不在憑證設計</span>
                </td>
                <td class="lim-col">
                  <input v-model="r.max" inputmode="numeric" placeholder="—" class="lim-in" :aria-label="`${r.label} 單張上限`" />
                </td>
                <td class="lim-col">
                  <input
                    v-model="r.max_delta"
                    inputmode="numeric"
                    placeholder="—"
                    class="lim-in"
                    :aria-label="`${r.label} 增量上限`"
                  />
                </td>
              </tr>
              <tr v-if="!limitRows.length">
                <td colspan="3" class="subtle">這個產品還沒有宣告任何配額。</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="subtle policy-note">
          單張上限＝一張子證這一項的值；增量上限＝取代舊張時「新值 − 被取代那張的值」（沒有取代對象時比單張的值）。
          只填一欄＝只檢查那一欄。
        </p>
        <p v-if="limitsBuilt.error" class="warn-text policy-note">{{ limitsBuilt.error }}</p>
        <div class="chk-block standalone">
          <label class="chk">
            <input v-model="planDraft.follow_main_expiry" type="checkbox" :disabled="!auth.isManagerOrAbove" />
            跟隨主證到期
          </label>
          <small class="chk-note">
            簽發時到期日＝這台主機現行主證的到期日；主證續約、延期、換證時自動換發新張（數值不變、到期日對齊新主證）。
          </small>
        </div>
      </template>
      <!-- 批110：這個套裝賣出去時 payload 的 `policy` 預設值（#5494 的「各套裝不同寬限期」）。 -->
      <template v-if="draftPolicies.length || (selected?.policies?.length ?? 0) > 0">
        <h4 class="sub-title">行為參數預設值</h4>
        <PolicyEditor :key="`p${planFormKey}`" v-model="planDraft.policies" :spec="selected" />
      </template>

      <template #foot>
        <UiButton @click="planOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!planValid" :loading="planSaving" @click="savePlan">
          儲存
        </UiButton>
      </template>
    </UiModal>

    <!-- 新增產品 -->
    <UiModal :open="createOpen" title="新增產品" width="560px" @close="createOpen = false">
      <div class="form-grid">
        <label class="field">
          <span class="field-label">產品代號（key）</span>
          <input v-model="createForm.key" placeholder="小寫英數與 - _，例：iplusone-pos" v-enter="doCreate" />
          <small class="subtle">建立後不可更改——它會逐字寫進每一張授權的明文。</small>
        </label>
        <label class="field">
          <span class="field-label">名稱</span>
          <input v-model="createForm.name" placeholder="例：愛+1 POS" v-enter="doCreate" />
        </label>
        <label class="field">
          <span class="field-label">備註</span>
          <input v-model="createForm.notes" placeholder="選填" v-enter="doCreate" />
        </label>
      </div>
      <p class="subtle">新產品的憑證設計是空的（沒有模組也沒有配額）；多數情況請改用既有產品的「以此為樣板」。</p>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="createBusy" @click="doCreate">建立</UiButton>
      </template>
    </UiModal>

    <!-- 以此為樣板 -->
    <UiModal
      :open="cloneOpen"
      :title="`以「${selected?.name ?? ''}」為樣板建立新產品`"
      width="560px"
      @close="cloneOpen = false"
    >
      <div class="form-grid">
        <label class="field">
          <span class="field-label">新產品代號（key）</span>
          <input v-model="cloneForm.key" placeholder="例：iplusone-pos" v-enter="doClone" />
        </label>
        <label class="field">
          <span class="field-label">新產品名稱</span>
          <input v-model="cloneForm.name" placeholder="例：愛+1 POS" v-enter="doClone" />
        </label>
      </div>
      <label class="chk standalone">
        <input v-model="cloneForm.include_plans" type="checkbox" />
        連同套裝一起複製（樣板的價值大半在這幾列）
      </label>
      <p class="subtle">簽章金鑰（kid）刻意不複製：新產品預設沿用系統金鑰，避免靜默共用某產品專屬的鑰。</p>
      <template #foot>
        <UiButton @click="cloneOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="cloneBusy" @click="doClone">建立</UiButton>
      </template>
    </UiModal>

    <!-- 匯入預覽（批107 項 7）：先看差異、勾選、再套用 -->
    <UiModal :open="delOpen" title="刪除產品" width="560px" @close="delOpen = false">
      <template v-if="delPreview">
        <p class="hint">
          即將刪除產品「<strong>{{ delPreview.name }}</strong>」（<code class="mono">{{ delPreview.key }}</code>）。
        </p>
        <p class="subtle">
          產品代號會逐字寫進每一張已簽發的憑證——刪掉之後那些證的模組／配額鍵就再也翻不回中文。
          只是要下架請改成「已停售」。
        </p>
        <div class="del-refs">
          <div v-for="(n, k) in delPreview.references" :key="k" class="del-ref" :class="{ hot: n > 0 }">
            <span>{{ refLabel(String(k)) }}</span><strong>{{ n }}</strong>
          </div>
        </div>
        <!-- 套裝與產品參數版本是產品自己的資料：不算引用、不擋刪除，但會一起消失（批151）。 -->
        <p v-if="cascadeText(delPreview)" class="subtle">
          會連帶刪除 {{ cascadeText(delPreview) }}。
        </p>
        <p v-if="delPreview.scope.admin_users || delPreview.scope.resellers" class="subtle">
          會從 {{ delPreview.scope.admin_users }} 個帳號、{{ delPreview.scope.resellers }} 家代理商的
          產品範圍移除<template v-if="delPreview.scope.scope_emptied">
            ——其中 {{ delPreview.scope.scope_emptied }} 個只剩這一個產品，<strong>範圍會變成空的＝暫停簽發權</strong></template>。
        </p>
        <p v-if="!delPreview.deletable" class="warn-text">
          ⚠️ 已經有 {{ delPreview.total_references }} 筆資料引用這個產品。強制刪除之後，
          那些憑證的模組／配額鍵<strong>再也翻不回中文</strong>、續約時的政策解析會退到最嚴。
          整份憑證設計會寫進稽核紀錄（`product.delete`）備查。
        </p>
        <label class="f">
          <span>請輸入 <code class="mono">{{ delPreview.key }}</code> 確認</span>
          <input v-model="delTyped" placeholder="產品代號" />
        </label>
      </template>
      <p v-else class="subtle">讀取中…</p>
      <template #foot>
        <UiButton @click="delOpen = false">取消</UiButton>
        <UiButton
          variant="danger"
          :loading="delBusy"
          :disabled="!delPreview || delTyped.trim() !== delPreview.key"
          @click="doDelete"
        >
          {{ delPreview && !delPreview.deletable ? '強制刪除' : '刪除' }}
        </UiButton>
      </template>
    </UiModal>

    <UiModal :open="importOpen" title="匯入產品設計" width="860px" @close="importOpen = false">
      <p class="subtle">
        檔案：<code class="mono">{{ importFileName }}</code>
        ——共 {{ importDiffs.length }} 個產品。勾選要套用的項目；
        <strong>簽章金鑰、「開放由我方代管」與產品狀態（上架／停售）不會被匯入</strong>，
        套裝只會新增與更新、<strong>不會刪除</strong>。代管設定：檔案有帶就整份取代、沒帶就保留這裡的現值。
      </p>
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table imp">
          <thead>
            <tr>
              <th class="w-chk"></th><th>產品</th><th>動作</th><th>差異</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in importDiffs" :key="d.key" :class="{ skip: !importSelectable(d) }">
              <td class="w-chk">
                <input
                  v-model="importPicked[d.key]"
                  type="checkbox"
                  :disabled="!importSelectable(d)"
                  :title="importSelectable(d) ? '套用這個產品' : '這一項沒有東西可以套用'"
                />
              </td>
              <td>
                <div>{{ d.name }}</div>
                <code class="mono">{{ d.key }}</code>
              </td>
              <td class="nowrap">
                <span class="status-pill" :class="d.action === 'create' ? 'active' : 'superseded'">
                  {{ IMPORT_ACTION_LABELS[d.action] ?? d.action }}
                </span>
              </td>
              <td>
                <div class="subtle">{{ summarizeDiff(d) }}</div>
                <!-- 「無變更」的列不列提醒：那幾句講的是「套用時會怎麼處理」，
                     對一個不會被套用的產品只是噪音（`forbidden` 的那句要留，它是原因）。 -->
                <p
                  v-for="(w, i) in d.action === 'unchanged' ? [] : d.warnings"
                  :key="`w${i}`"
                  class="imp-warn"
                >
                  {{ w }}
                </p>
                <p v-for="(g, i) in d.dangers" :key="`d${i}`" class="imp-danger">⚠ {{ g }}</p>
              </td>
            </tr>
            <tr v-if="!importDiffs.length">
              <td colspan="4" class="subtle">這份檔案沒有任何產品。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="warn-text">
        ⚠ 套用只影響<strong>之後核發</strong>的證；既有授權是簽好的快照，內容不會改變。
      </p>
      <template #foot>
        <UiButton @click="importOpen = false">取消</UiButton>
        <UiButton
          variant="primary"
          :disabled="!importPickedKeys.length"
          :loading="importBusy"
          @click="doImport"
        >
          套用 {{ importPickedKeys.length }} 個產品
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
/* 批169：配額視窗兩格文字欄的就地錯誤（同 `CustomerProfileCard` 的 `.err`）。 */
.err-text {
  color: var(--danger);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.ro-banner {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: var(--sp-4);
  align-items: start;
}
/* 手機／平板（批171）：右欄的 `1fr` 是 `minmax(auto, 1fr)`，會被卡片內表格的最小寬度撐開、整欄超出畫面
   （1024 寬時名稱欄、備註、〈儲存〉與第五個頁籤都被切掉）→ 釘成 `minmax(0, 1fr)`。 */
@media (--compact) {
  .grid {
    grid-template-columns: 280px minmax(0, 1fr);
  }
  .grid > .card {
    min-width: 0;
  }
}
/* 批155：窄螢幕（768 等）左右兩欄改成上下疊，右卡收住（表格改在卡片內橫捲），
   與〈客戶〉頁同一個 900px 斷點。 */
@media (max-width: 900px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .grid > .card {
    min-width: 0;
  }
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 2px;
}
.list li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px var(--sp-2);
  padding: 8px 10px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.list li:hover {
  background: var(--bg-hover);
}
.list li.sel {
  background: var(--brand-100);
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .list li.sel {
  background: var(--bg-active);
  color: var(--brand-400);
}
.pname {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list code {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.list .cnt {
  grid-column: 1 / -1;
  font-size: var(--fs-11);
  color: var(--fg-4);
  font-weight: 400;
}
.dep-tag {
  font-size: var(--fs-11);
  color: var(--warning);
}
/* 批141：開放代管卻沒有有效的代管設定（其實開不了站）。 */
.hp-broken {
  font-size: var(--fs-11);
  color: var(--danger);
}
.hp-broken-text {
  margin: var(--sp-2) 0 0;
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.card-head.sub {
  margin-top: var(--sp-4);
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.form-grid.three {
  /* 批97：190px 塞不下帶 spinner 的 type=number（產品選項七欄有四個是），欄位互相重疊。
     220px 對套裝彈窗（820px）仍是三欄；對右卡的「產品選項」區則自然降成兩欄。 */
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.field.notes {
  margin-top: var(--sp-3);
}
.field textarea {
  font-family: inherit;
  font-size: var(--fs-13);
  padding: 8px 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  color: var(--fg);
  resize: vertical;
}
.acts-row {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-4);
}
.w-drag {
  width: 26px;
}
.nowrap {
  white-space: nowrap;
}
.drag {
  cursor: grab;
  color: var(--fg-4);
  user-select: none;
}
.chip.dep {
  margin-left: 6px;
  color: var(--warning);
  border-color: var(--warning);
}
/* 批114：配額規則旗標。多個並排時給一點間距，必填用與「已達上限」同一組紅。 */
.chip + .chip {
  margin-left: 4px;
}
.chip.req {
  color: var(--danger);
  border-color: rgba(209, 67, 67, 0.45);
}
/* 批151：字串行為參數「可產生唯一值」。 */
.chip.gen {
  margin-left: 6px;
}
/* 批155：內部模組／內部套裝與自動核准的標記。 */
.chip.int {
  margin-left: 6px;
  color: var(--brand-600, var(--fg-2));
  border-color: var(--brand-400, var(--border));
}
.plan-flags .chip.int {
  margin-left: 0;
}
.chip.aa {
  color: var(--st-active-fg, var(--fg-2));
  border-color: var(--st-active-fg, var(--border));
}
.plan-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.plan-flags .chip + .chip {
  margin-left: 0;
}
/* 批155：勾選框＋固定在底下的說明（說明不隨狀態出現／消失，切換時版面不動）。 */
.chk-block {
  display: grid;
  gap: 2px;
}
.chk-block.standalone {
  margin-top: var(--sp-3);
}
.chk-note {
  padding-left: 22px;
  color: var(--fg-4);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.chk.off {
  cursor: not-allowed;
  opacity: 0.6;
}
.tbl.lim .lim-col {
  width: 150px;
}
.tbl.lim tr.bad td {
  color: var(--fg-4);
}
.lim-in {
  width: 120px;
  text-align: right;
}
/* 套裝表的配額摘要欄：窄欄一行不折行，太長由 `QuotaSummary` 自己收省略號。 */
.w-quota {
  max-width: 240px;
}
.hint {
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.policy-hint {
  font-size: var(--fs-11);
  line-height: var(--lh-base);
  max-width: 240px;
}
.policy-note {
  margin-top: var(--sp-2);
}
/* 表格內的小圖示按鈕（與 CustomersView 的 `.x` 同一套） */
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.x.danger:hover {
  color: var(--danger);
}
.checks {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
  margin-top: var(--sp-3);
}
/* 批114：說明較長的一組（配額規則旗標）一列一個，靠 wrap 排會斷得很難看。 */
/* 批153：市集型錄代號放在勾選列下面，跟勾選列隔一段。 */
.market-catalog {
  margin-top: var(--sp-3);
}
.checks.stack {
  flex-direction: column;
  gap: var(--sp-2);
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
}
.chk.standalone {
  margin-top: var(--sp-3);
}
.save-bar {
  margin-top: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border);
}
.warn-text {
  margin: 0;
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.item-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-2);
}
.it-key {
  width: 170px;
}
.it-label {
  flex: 1;
  min-width: 140px;
}
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
/* --- 匯入預覽表 --- */
.w-chk {
  width: 32px;
}
.tbl.imp td {
  vertical-align: top;
}
.tbl.imp tr.skip {
  opacity: 0.6;
}
.imp-warn,
.imp-danger {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.imp-warn {
  color: var(--fg-3);
}
.imp-danger {
  color: var(--danger);
}

/* 批110 立、批112 改：刪除產品只是一顆一般按鈕，放在儲存那一列的最左邊（margin-right:auto 把
   右邊那串推到底），紅框與整段警語拿掉——警語在按下後的確認視窗
   （主人 09-17：「按了才跳出來警告就好」「移到左下」）。 */
.acts-row .del-btn {
  margin-right: auto;
}
.del-refs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 6px;
  margin: var(--sp-3) 0;
}
.del-ref {
  display: flex;
  justify-content: space-between;
  padding: 4px 8px;
  border: 1px solid var(--border-2);
  border-radius: var(--r-sm);
  font-size: var(--fs-12);
}
.del-ref.hot {
  border-color: var(--danger);
  color: var(--danger);
}
</style>
